import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../i18n';
import { useNotifications } from '../hooks/useNotifications';
import Layout from '../components/Layout';
import { warehouseAPI, salesAPI, productionAPI, qualityAPI, exportAPI, printLabel,
         gradeAPI, sheetAPI, printSheetLabelBig, shipmentAPI,
         dspDefectAPI, shipNoteAPI } from '../api';
import toast from 'react-hot-toast';
import StockFreshness from '../components/StockFreshness';
import { confirmToast } from '../components/ConfirmToast';
import ClientPicker from '../components/ClientPicker';
import { makeMatcher } from '../utils/search';
import { openCard, openDivider } from '../components/openCard';

// ─── Общие стили ─────────────────────────────────────────────────────────────
// DRF отдаёт ошибку то строкой, то {error}, то {поле: [строка]} — без разбора
// пользователь видит «[object Object]» вместо причины.
const errText = (err, fallback) => {
  const d = err && err.response && err.response.data;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (d.error) return String(d.error);
  const first = Object.values(d)[0];
  return Array.isArray(first) ? String(first[0]) : (first ? String(first) : fallback);
};

const S = {
  card: { background: 'var(--card)', borderRadius: 12, padding: 20 },
  input: {
    width: '100%', padding: '9px 12px', background: 'var(--bg)',
    border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
  },
  label: { display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 5 },
  modalBack: {
    position: 'fixed', inset: 0, background: '#000000cc', zIndex: 400,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  modalBox: {
    background: 'var(--card)', borderRadius: 16, padding: 20,
    width: '100%', maxHeight: '94vh', overflowY: 'auto',
  },
  btn: (color = 'var(--accent)', disabled = false) => ({
    background: disabled ? 'var(--card)' : color,
    border: disabled ? '1px solid var(--border)' : 'none',
    borderRadius: 8, color: disabled ? 'var(--mid)' : '#fff',
    padding: '10px 20px', fontSize: 14, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
};

const Field = ({ label, children }) => (
  <div>
    <label style={S.label}>{label}</label>
    {children}
  </div>
);

const Select = ({ label, value, onChange, children, ...props }) => (
  <Field label={label}>
    <select value={value} onChange={onChange} style={S.input} {...props}>{children}</select>
  </Field>
);

// ═══════════════════════════════════════════════════════════════════
// ТАБ 1 — ОСТАТКИ СЫРЬЯ
// ═══════════════════════════════════════════════════════════════════

// ─── Остатки в разрезе складов ───────────────────────────────────────────────
//
// Обратная сторона разреза в «Остатках»: там от номенклатуры смотрят, на каких
// складах она лежит, здесь от склада — что на нём есть. Кладовщику нужны оба
// взгляда: первый отвечает «откуда минус по позиции», второй — «что вообще
// числится на этом складе».
//
// Складами MES не управляет, их ведёт 1С — экран только показывает. Коды видны
// потому, что по ним сверяются с 1С: названия там правят, коды остаются.
const WH_KIND_ICON = { paper: '📄', dsp: '🪵', ldsp: '📦' };

function StockByWarehouse() {
  const [rows, setRows]   = useState([]);
  const [open, setOpen]   = useState(null);
  const [q, setQ]         = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    warehouseAPI.getStockByWarehouse()
      .then(r => setRows(r.data || []))
      .catch(() => toast.error(t('Остатки по складам не загрузились')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 24 }}>Загрузка…</p>;
  if (!rows.length) return <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 24 }}>Складов с остатками нет</p>;

  const needle = q.trim().toLowerCase();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map(w => {
        const isOpen = open === w.id;
        const items = needle
          ? w.items.filter(i => makeMatcher(needle)(i.name))
          : w.items;
        return (
          <div key={w.id || 'none'} style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
            <div
              onClick={() => { setOpen(isOpen ? null : w.id); setQ(''); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                padding: '14px 16px', cursor: 'pointer',
              }}>
              <span style={{ color: 'var(--dim)', fontSize: 13 }}>{isOpen ? '▾' : '▸'}</span>
              <span style={{ fontSize: 20 }}>{WH_KIND_ICON[w.kind] || '🏬'}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>{w.name}</div>
                <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 2 }}>
                  {/* Тип задаётся в MES: по нему выбирается склад списания в 1С */}
                  {w.kind_display || 'Тип не указан'}
                  {' · MES: '}<b style={{ fontFamily: 'monospace', color: 'var(--muted)' }}>{w.mes_code}</b>
                  {w.code
                    ? <> · 1С: <b style={{ fontFamily: 'monospace', color: 'var(--muted)' }}>{w.code}</b></>
                    : <span style={{ color: '#f59e0b' }}> · 1С код не пришёл</span>}
                </div>
              </div>
              {w.negative > 0 && (
                <span style={{
                  background: '#ef444420', color: '#ef4444', borderRadius: 6,
                  padding: '2px 9px', fontSize: 12, fontWeight: 700,
                }}>минус: {w.negative}</span>
              )}
              <span style={{ color: 'var(--muted)', fontSize: 13 }}>{w.positions} поз.</span>
            </div>

            {isOpen && (
              <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px' }}>
                <input
                  value={q} onChange={e => setQ(e.target.value)}
                  placeholder={t('Поиск номенклатуры на этом складе…')}
                  style={{ ...S.input, marginBottom: 10 }}
                />
                {items.length === 0 ? (
                  <p style={{ color: 'var(--mid)', fontSize: 13, textAlign: 'center', padding: 12 }}>
                    Ничего не найдено
                  </p>
                ) : (
                  <div style={{ maxHeight: '52vh', overflowY: 'auto' }}>
                    {items.map(i => (
                      <div key={i.id} style={{
                        display: 'flex', alignItems: 'baseline', gap: 12,
                        padding: '6px 0', borderBottom: '1px solid var(--border)',
                      }}>
                        <span style={{ color: 'var(--muted)', fontSize: 13, flex: 1, minWidth: 0 }}>{i.name}</span>
                        <b style={{
                          color: i.quantity < 0 ? '#ef4444' : 'var(--text)',
                          fontSize: 14, whiteSpace: 'nowrap',
                        }}>{i.quantity.toLocaleString('ru-RU')} {i.unit}</b>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StockSummary({ refresh, onRefresh }) {
  const [stock, setStock]         = useState([]);
  const [loadingStock, setLS]     = useState(true);
  const [receipts, setReceipts]   = useState([]);
  const [loadingRec, setLR]       = useState(true);
  const [search, setSearch]       = useState('');
  const [collapsed, setCollapsed] = useState(false);
  // раскрытый разрез по складам — только один за раз, чтобы список не разъезжался
  const [openWh, setOpenWh]       = useState(null);
  const [cancelling, setCancelling] = useState(null);
  // Справочник ведёт 1С, и позиций в нём сотни — нулевых большинство. Склад
  // приходит сюда за тем, что на полке лежит, поэтому пустые строки по
  // умолчанию скрыты. Понадобились — одна кнопка.
  const [onlyStock, setOnlyStock] = useState(true);
  const role = sessionStorage.getItem('role');
  const isAdmin   = role === 'admin';
  const canView   = ['admin', 'manager', 'sklad_gp'].includes(role);

  const loadStock = () => {
    warehouseAPI.getStockSummary().then(r => setStock(r.data)).finally(() => setLS(false));
  };
  const loadReceipts = () => {
    if (!canView) return;
    warehouseAPI.getReceipts().then(r => setReceipts(r.data)).finally(() => setLR(false));
  };

  useEffect(() => { loadStock(); }, [refresh]); // eslint-disable-line
  useEffect(() => { loadReceipts(); }, [refresh]); // eslint-disable-line

  const handleCancel = async (id) => {
    if (!await confirmToast('Отменить документ? Приход будет снят с остатка.', { danger: true, confirmText: 'Отмена', cancelText: 'Нет' })) return;
    setCancelling(id);
    try {
      await warehouseAPI.cancelReceipt(id);
      toast.success(t("Документ отменён"));
      loadStock();
      loadReceipts();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Ошибка');
    } finally { setCancelling(null); }
  };

  return (
    <div>
      {/* Qoldiqlar */}
      <div style={S.card}>
        <div
          onClick={() => setCollapsed(!collapsed)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsed ? 0 : 16, cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 16 }}>
            📦 Остатки сырья ({stock.filter(x => !onlyStock || x.total_quantity || x.is_negative).length})
            {onlyStock && stock.length > 0 && (
              <span style={{ color: 'var(--dim)', fontWeight: 500, fontSize: 12.5, marginLeft: 8 }}>
                {t('из')} {stock.length}
              </span>
            )}
            {/* Числа здесь — из 1С. Если обмен встал, они вчерашние, и знать
                об этом надо тому, кто на них смотрит. */}
            <div onClick={e => e.stopPropagation()} style={{ marginTop: 5 }}>
              <StockFreshness />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setOnlyStock(v => !v)}
              title={t('Показать и позиции с нулевым остатком')}
              style={{
                background: onlyStock ? 'var(--accent)' : 'var(--bg)',
                border: '1px solid ' + (onlyStock ? 'var(--accent)' : 'var(--border)'),
                borderRadius: 7, color: onlyStock ? '#fff' : 'var(--muted)',
                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
              }}>
              {onlyStock ? t('только с остатком') : t('все позиции')}
            </button>
            <input
              type="text"
              placeholder={t("🔍 Поиск...")}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              style={{
                width: 160, padding: '6px 10px', background: 'var(--bg)',
                border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text)',
                fontSize: 12, outline: 'none',
              }}
            />
            <span style={{ color: 'var(--dim)', fontSize: 16, transition: 'transform 0.2s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▾</span>
          </div>
        </div>
        {!collapsed && (loadingStock ? (
          <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 20 }}>Загрузка...</p>
        ) : stock.length === 0 ? (
          <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 20 }}>{t('Данных нет')}</p>
        ) : (
          (() => {
            // Поиск важнее фильтра: если ищут по имени, показываем и нулевые —
            // иначе «ничего не найдено» на позицию, которая в справочнике есть.
            const base = search.trim()
              ? stock.filter(item => makeMatcher(search)(item.name, item.unit))
              : stock;
            const filtered = (search.trim() || !onlyStock)
              ? base
              : base.filter(item => item.total_quantity || item.is_negative);
            return filtered.length === 0 ? (
              <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 20 }}>{t('Ничего не найдено')}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filtered.map(item => {
                  const neg = item.is_negative;
                  const pct = item.total_quantity > 0
                    ? Math.min(100, Math.round((item.available_quantity / item.total_quantity) * 100)) : 0;
                  const barColor = neg ? '#ef4444' : pct > 50 ? '#22c55e' : pct > 20 ? '#f59e0b' : '#ef4444';
                  const whs = item.warehouses || [];
                  const open = openWh === item.id;
                  return (
                    <div key={item.id} style={{
                      background: neg ? '#ef44440e' : 'var(--bg)', borderRadius: 8, padding: '10px 14px',
                      border: `1px solid ${neg ? '#ef4444' : 'transparent'}`,
                    }}>
                      {/* Складами MES не управляет — их ведёт 1С. Но сумма по всем
                          складам прячет, ГДЕ провал: раскрываем разрез по клику. */}
                      <div
                        onClick={() => whs.length > 0 && setOpenWh(open ? null : item.id)}
                        style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 6, cursor: whs.length > 0 ? 'pointer' : 'default' }}
                      >
                        <span style={{ color: 'var(--text)', fontSize: 14 }}>
                          {whs.length > 0 && <span style={{ color: 'var(--dim)', marginRight: 6, fontSize: 11 }}>{open ? '▾' : '▸'}</span>}
                          {item.name}
                          {neg && <span style={{ marginLeft: 8, background: '#ef4444', color: '#fff', borderRadius: 6, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>минус</span>}
                        </span>
                        <div style={{ display: 'flex', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
                          <span style={{ color: 'var(--mid)' }}>Всего: <b style={{ color: 'var(--muted)' }}>{item.total_quantity} {item.unit}</b></span>
                          <span style={{ color: 'var(--mid)' }}>Резерв: <b style={{ color: '#f59e0b' }}>{item.reserved_quantity} {item.unit}</b></span>
                          <span style={{ color: 'var(--mid)' }}>Свободно: <b style={{ color: barColor }}>{item.available_quantity} {item.unit}</b></span>
                        </div>
                      </div>
                      <div style={{ background: 'var(--card)', borderRadius: 4, height: 4 }}>
                        <div style={{ width: `${pct}%`, height: 4, background: barColor, borderRadius: 4 }} />
                      </div>
                      {open && (
                        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ color: 'var(--dim)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>По складам (из 1С)</div>
                          {whs.map(w => (
                            <div key={w.id || 'none'} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, fontSize: 13, padding: '3px 0' }}>
                              <span style={{ color: 'var(--muted)', minWidth: 0 }}>{w.name}</span>
                              {/* Код склада из 1С: по названию склад не найти —
                                  1С их правит, а код остаётся прежним */}
                              {w.code && (
                                <span style={{ color: 'var(--dim)', fontSize: 11, fontFamily: 'monospace', marginLeft: 'auto' }}>{w.code}</span>
                              )}
                              <b style={{ color: w.quantity < 0 ? '#ef4444' : 'var(--text)', marginLeft: w.code ? 0 : 'auto' }}>{w.quantity} {item.unit}</b>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()
        ))}
      </div>

      {/* Kirim hujjatlari */}
      {canView && (
        <div style={{ ...S.card, marginTop: 16 }}>
          <div
            onClick={() => setCollapsed(!collapsed)}
            style={{ color: 'var(--text)', fontWeight: 700, fontSize: 16, marginBottom: collapsed ? 0 : 14, cursor: 'pointer', userSelect: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span>📋 Документы прихода ({receipts.length})</span>
            <span style={{ color: 'var(--dim)', fontSize: 16, transition: 'transform 0.2s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▾</span>
          </div>
          {!collapsed && (loadingRec ? (
            <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 16 }}>Загрузка...</p>
          ) : receipts.length === 0 ? (
            <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 16 }}>{t('Документов нет')}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {receipts.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg)', borderRadius: 10, padding: '12px 14px',
                  border: `1px solid ${r.is_cancelled ? '#ef444430' : 'var(--border)'}`,
                  opacity: r.is_cancelled ? 0.7 : 1,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <span style={{ color: 'var(--text)', fontWeight: 600 }}>Документ №{r.id}</span>
                      {r.is_cancelled && (
                        <span style={{ marginLeft: 10, background: '#ef444420', color: '#ef4444', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                          ОТМЕНЁН
                        </span>
                      )}
                      {r.is_from_1c && (
                        <span title={t("Документ из 1С — отменять только в 1С")} style={{ marginLeft: 10, background: '#0ea5e920', color: '#0ea5e9', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                          1С
                        </span>
                      )}
                    </div>
                    {isAdmin && !r.is_cancelled && !r.is_from_1c && (
                      <button
                        onClick={() => handleCancel(r.id)}
                        disabled={cancelling === r.id}
                        style={{ background: 'none', border: '1px solid #ef4444', borderRadius: 6, color: '#ef4444', padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
                      >
                        {cancelling === r.id ? '...' : 'Отмена'}
                      </button>
                    )}
                  </div>

                  <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 4 }}>
                    Внёс: <b style={{ color: 'var(--muted)' }}>{r.created_by_name}</b>
                    {' · '}
                    {new Date(r.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {r.is_cancelled && r.cancelled_by_name && (
                      <span style={{ marginLeft: 10, color: '#ef4444' }}>
                        · Отменил: {r.cancelled_by_name}
                        {' '}{new Date(r.cancelled_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>

                  {r.note && <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 3, fontStyle: 'italic' }}>💬 {r.note}</div>}

                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {r.items.map((it, idx) => (
                      <div key={idx} style={{ fontSize: 13, color: 'var(--muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <span>• {it.nomenclature_name}</span>
                        <b style={{ color: 'var(--text)' }}>{it.quantity} {it.unit}</b>
                        {it.supplier_name && <span style={{ color: 'var(--dim)' }}>Поставщик: {it.supplier_name}</span>}
                        {it.batch_number && <span style={{ color: 'var(--dim)' }}>Партия: {it.batch_number}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ТАБ: ОТГРУЗКА — готовые заказы отгружает склад ГП (перенесено с менеджера)
// Модаль отгрузки: показывает состав заказа и требует сканирования паллет по QR.
function ShipScanModal({ order, onClose, onDone }) {
  const [items, setItems]       = useState(null);
  const [rows, setRows]         = useState([]);    // йиғилган позициялар
  const [code, setCode]         = useState('');
  const [found, setFound]       = useState(null);  // сканерланган лист
  const [qty, setQty]           = useState('');
  const [busy, setBusy]         = useState(false);
  const [shipping, setShipping] = useState(false);
  // Бирка — не ключ к листу, а способ назвать пару «продукт + сорт»: списание
  // всё равно берёт самые старые листы этой пары. Значит ту же пару можно
  // выбрать руками, а сканер оставить на случай, когда лист под рукой. Без
  // этого отгрузить было нельзя вообще, если бирка потерялась или стёрлась.
  const [stock, setStock]       = useState(null);
  const [pick, setPick]         = useState('');
  const inputRef = useRef(null);
  const qtyRef   = useRef(null);

  useEffect(() => {
    salesAPI.getOrder(order.id)
      .then(r => setItems(r.data.items || []))
      .catch(() => { setItems([]); toast.error(t("Состав заказа не загрузился")); });
    shipmentAPI.available({ order: order.id })
      .then(r => setStock(r.data.results || []))
      .catch(() => setStock([]));
  }, [order.id]);
  useEffect(() => { inputRef.current && inputRef.current.focus(); }, []);
  const focusInput = () => inputRef.current && inputRef.current.focus();

  // шу ойнада йиғилган сон
  const takenByProduct = rows.reduce((a, r) => {
    a[r.nomenclature_id] = (a[r.nomenclature_id] || 0) + r.qty; return a;
  }, {});
  // Аввалги жўнатмаларда берилгани (базадан) + ҳозир йиғилгани.
  // Буюртма бўлиб-бўлиб берилиши мумкин — қолгани шу ерда ҳисобланади.
  const shippedBefore = (nomId) => {
    const oi = (items || []).find(x => String(x.product) === String(nomId));
    return oi ? (oi.quantity_shipped || 0) : 0;
  };
  const totalTaken = (nomId) =>
    shippedBefore(nomId) + (takenByProduct[nomId] || 0);
  // маҳсулот+сорт бўйича — омбор қолдиғидан ошиб кетмаслиги учун
  const takenByKey = rows.reduce((a, r) => {
    a[r.key] = (a[r.key] || 0) + r.qty; return a;
  }, {});

  const orderItemOf = (nomId) =>
    (items || []).find(it => String(it.product) === String(nomId));

  // Лист биркаси сканерланади — ундан маҳсулот ва СОРТ аниқланади
  const scan = async (raw) => {
    const qr = (raw || '').trim();
    if (!qr) return;
    setBusy(true);
    try {
      const { data } = await sheetAPI.lookup(qr);
      setCode('');
      // Брак отгружаем, но молча — нет: кладовщик должен понимать, что
      // подносит бирку бракованного листа, а не первого сорта.
      if (data.is_defect) {
        toast(t('⚠ Это БРАК — отгрузить можно, но это брак'),
              { id: 'defect-scan', icon: '⚠️' });
      }
      if (!data.nomenclature_id) {
        toast.error(t("❌ Продукт листа не определён")); focusInput(); return;
      }
      if (!orderItemOf(data.nomenclature_id)) {
        toast.error(t("❌ Этого продукта нет в заказе")); focusInput(); return;
      }
      setFound(data);
      setQty('');
      setTimeout(() => qtyRef.current && qtyRef.current.focus(), 60);
    } catch {
      setCode('');
      toast.error(t("❌ Лист не найден"));
      focusInput();
    } finally { setBusy(false); }
  };

  const addRow = () => {
    if (!found) return;
    const n = Number(qty);
    if (!n || n <= 0) { toast.error(t("Укажите количество")); return; }

    const oi      = orderItemOf(found.nomenclature_id);
    const ordered = oi ? oi.quantity : 0;
    const already = totalTaken(found.nomenclature_id);
    if (already + n > ordered) {
      toast.error(`В заказе ${ordered} шт — отгрузить можно не больше ${Math.max(0, ordered - already)} шт`);
      return;
    }
    const key     = `${found.nomenclature_id}|${found.grade_type_id ?? found.grade}`;
    const inStock = found.available || 0;
    if ((takenByKey[key] || 0) + n > inStock) {
      toast.error(`На складе ${inStock} шт (${found.grade_name})`);
      return;
    }
    setRows(prev => [{
      id: Date.now(),
      key,
      nomenclature_id: found.nomenclature_id,
      product:    found.product_name,
      grade:      found.grade,
      grade_name: found.grade_name,
      is_defect:  !!found.is_defect,
      sheet_qr:   found.qr_code,
      qty:        n,
      available:  inStock,
    }, ...prev]);
    setFound(null); setQty('');
    focusInput();
  };

  const remove = (id) => setRows(prev => prev.filter(r => r.id !== id));

  // Больше, чем лежит на складе по этой паре, и больше, чем осталось по
  // заказу, взять нельзя — предел считаем без самой правимой строки.
  const maxFor = (row) => {
    const otherKey = rows.filter(r => r.id !== row.id && r.key === row.key)
      .reduce((a, r) => a + r.qty, 0);
    const byStock  = Math.max(0, (row.available || 0) - otherKey);
    const oi       = orderItemOf(row.nomenclature_id);
    const ordered  = oi ? oi.quantity : 0;
    const otherNom = rows.filter(r => r.id !== row.id
                                   && String(r.nomenclature_id) === String(row.nomenclature_id))
      .reduce((a, r) => a + r.qty, 0);
    const byOrder  = Math.max(0, ordered - shippedBefore(row.nomenclature_id) - otherNom);
    return Math.min(byStock, byOrder);
  };

  const editQty = (row, raw) => {
    const lim = maxFor(row);
    let n = parseInt(raw, 10);
    if (!Number.isFinite(n)) n = 0;
    if (n > lim) { n = lim; toast.error(`Больше ${lim} шт нельзя`); }
    setRows(prev => prev.map(r => r.id === row.id ? { ...r, qty: Math.max(0, n) } : r));
  };

  const total  = rows.reduce((s, r) => s + r.qty, 0);

  // Весь остаток заказа одной кнопкой — грузят машину целиком, а не по листу
  const takeAll = () => {
    const picked = pickAllForOrder(items, stock, takenByKey, takenByProduct);
    if (!picked.length) {
      toast.error(t('Добирать нечего: заказ собран или на складе пусто'));
      return;
    }
    setRows(prev => [...picked, ...prev]);
    const n = picked.reduce((a, r) => a + r.qty, 0);
    toast.success(`Собрано ${n} шт — проверьте и нажмите «Отгрузить»`);
  };

  const shipAll = async () => {
    if (!rows.length) { toast.error(t("Ничего не собрано")); return; }
    if (!await confirmToast(
      `Отгрузить ${total} шт по заказу ${order.order_number}?`,
      { confirmText: 'Отгрузить' }
    )) return;
    setShipping(true);
    try {
      // Заказ закрывает БЭКЕНД — и только когда отгружено всё.
      // Если взяли часть, заказ останется в «Отгрузке» с остатком.
      let closed = false;
      for (const r of rows) {
        const { data } = await shipmentAPI.shipBySheet({
          sheet_qr: r.sheet_qr, quantity: r.qty, order: order.id,
        });
        if (data && data.order_closed) closed = true;
      }
      if (closed) {
        toast.success(`✅ Заказ ${order.order_number} отгружен полностью (${total} шт)`);
      } else {
        toast.success(`✅ Отгружено ${total} шт. Заказ ${order.order_number} остаётся — отгружен частично`);
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Ошибка отгрузки');
    } finally { setShipping(false); }
  };

  return (
    <div className="wh-modal-wrap" style={{
      position: 'fixed', inset: 0, background: '#000000cc',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 20,
    }}>
      <div className="wh-modal" style={{ background: 'var(--card)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 1000, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ color: 'var(--text)', fontSize: 17, fontWeight: 700 }}>🚛 Отгрузка — {order.order_number}</h3>
            <p style={{ color: 'var(--dim)', fontSize: 13, marginTop: 3 }}>{order.client_name || (order.client && order.client.name) || '—'}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--dim)', fontSize: 24, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Слева — что нужно отдать, справа — чем набираем. Раньше это шло
            одной лентой вниз: состав сверху, собранное внизу, и кладовщик
            прокручивал окно туда-сюда, хотя смотреть надо на оба сразу. */}
        <div className="wh-two-col">
          <div style={{ minWidth: 0 }}>

        {/* Состав заказа */}
        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{t('📋 СОСТАВ ЗАКАЗА')}</div>
          {items === null ? (
            <p style={{ color: 'var(--mid)', fontSize: 13 }}>{t('Загрузка…')}</p>
          ) : items.length === 0 ? (
            <p style={{ color: 'var(--mid)', fontSize: 13 }}>{t('Позиции не найдены')}</p>
          ) : items.map(it => {
            const loaded = totalTaken(it.product);
            const enough = loaded >= it.quantity;
            return (
              <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--text)', fontSize: 14 }}>
                  {it.product_name}
                  {it.texture_name && <span style={{ color: 'var(--dim)', fontSize: 12 }}> · {it.texture_name}</span>}
                </span>
                <span style={{ fontSize: 13, color: enough ? '#22c55e' : 'var(--muted)', fontWeight: 700 }}>
                  {enough ? '✅ ' : ''}{loaded} / {it.quantity} шт
                </span>
              </div>
            );
          })}
        </div>

        {/* Собрать всё сразу. Кладовщик набирал заказ по одной позиции, хотя
            что нужно — написано в заказе, а что есть — известно складу. */}
        {items && items.length > 0 && stock !== null && stock.length > 0 && (
          <button onClick={takeAll} disabled={shipping} className="touch-btn" style={{
            width: '100%', marginBottom: 16, padding: '13px 0',
            background: 'var(--accent-weak)', border: '1px solid var(--accent)',
            borderRadius: 10, color: 'var(--accent)', fontSize: 15, fontWeight: 800,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>✅ {t('Взять всё по заказу')}</button>
        )}

          </div>

          <div style={{ minWidth: 0 }}>
        {/* Сканирование бирки ЛИСТА */}
        <label style={S.label}>{t('QR-код листа')}</label>
        <input ref={inputRef} style={{ ...S.input, fontSize: 16, fontFamily: 'monospace' }}
          value={code} disabled={busy || shipping}
          onChange={e => setCode(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); scan(code); } }}
          placeholder={t("Сканируйте бирку листа…")} autoFocus />
        <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 6, marginBottom: 12 }}>
          💡 Сканируется <b>лист</b>, а не паллета — по нему определяются продукт и сорт.
        </div>

        {/* Выбор руками. Бирка может стереться, потеряться или просто лежать в
            середине пачки — а отгрузить надо. Показываем только то, что на
            складе действительно свободно: выбрать несуществующее нельзя. */}
        {stock !== null && (
          stock.length === 0 ? (
            <div style={{
              background: '#f59e0b14', border: '1px solid #f59e0b40', borderRadius: 9,
              padding: '10px 13px', fontSize: 12.5, color: 'var(--text)', marginBottom: 14,
            }}>
              {t('По этому заказу на складе сейчас ничего нет — отгружать нечего.')}
            </div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              <label style={{ ...S.label, marginBottom: 6, display: 'block' }}>
                {t('…или выберите из того, что лежит на складе')}
              </label>
              <input
                value={pick} onChange={e => setPick(e.target.value)}
                placeholder={t('Поиск по названию…')}
                style={{ ...S.input, marginBottom: 6 }} />
              <div style={{ maxHeight: 190, overflowY: 'auto', display: 'flex',
                            flexDirection: 'column', gap: 4 }}>
                {stock
                  .filter(r => !pick.trim()
                    || (r.product_name + ' ' + r.short_name).toLowerCase()
                         .includes(pick.trim().toLowerCase()))
                  .map(r => {
                    const left = Math.max(0, r.ordered - totalTaken(r.nomenclature_id));
                    return (
                      <button key={`${r.nomenclature_id}|${r.grade_type_id ?? r.grade}`}
                        onClick={() => {
                          setFound(r); setQty('');
                          setTimeout(() => qtyRef.current && qtyRef.current.focus(), 60);
                        }}
                        disabled={busy || shipping}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                          textAlign: 'left', background: 'var(--bg)', borderRadius: 8,
                          border: '1px solid var(--border)', padding: '8px 11px',
                          cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text)',
                        }}>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                          {r.short_name || r.product_name}
                          <span style={{ color: 'var(--dim)', fontSize: 11.5 }}>
                            {' · '}{r.grade_name}
                          </span>
                          {r.is_defect && <DefectTag />}
                        </span>
                        <span style={{ color: 'var(--dim)', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                          {t('осталось по заказу')}: <b style={{ color: left ? 'var(--text)' : '#22c55e' }}>{left}</b>
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {r.available} {t('шт')}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </div>
          )
        )}

        {/* Найденный продукт — ввод количества */}
        {found && (() => {
          const oi      = orderItemOf(found.nomenclature_id);
          const ordered = oi ? oi.quantity : 0;
          const already = totalTaken(found.nomenclature_id);
          const canMax  = Math.max(0, Math.min(ordered - already,
                            (found.available || 0) - (takenByKey[`${found.nomenclature_id}|${found.grade_type_id ?? found.grade}`] || 0)));
          return (
            <div style={{ background: 'var(--accent-weak)', border: '1px solid var(--accent)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
              <div style={{ color: 'var(--text)', fontSize: 15, fontWeight: 700 }}>{found.product_name}</div>
              <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 4, marginBottom: 12 }}>
                Сорт: <b style={{ color: found.is_defect ? '#ef4444' : 'var(--text)' }}>
                  {found.grade_name}</b>{found.is_defect && <DefectTag />}
                {' · '}на складе: <b style={{ color: 'var(--text)' }}>{found.available} шт</b>
                {' · '}по заказу осталось: <b style={{ color: 'var(--text)' }}>{Math.max(0, ordered - already)} шт</b>
              </div>
              <label style={S.label}>{t('Сколько отгрузить')}</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <input ref={qtyRef} type="number" min="1" max={canMax}
                  style={{ ...S.input, flex: 1, fontSize: 16 }}
                  value={qty} onChange={e => setQty(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRow(); } }}
                  placeholder={`макс. ${canMax}`} />
                <button onClick={addRow} disabled={!canMax}
                  style={{ ...S.btn('#22c55e', !canMax), padding: '0 22px', fontSize: 14 }}>
                  ➕ Добавить
                </button>
                <button onClick={() => { setFound(null); setQty(''); focusInput(); }}
                  style={{ ...S.btn('var(--card)'), padding: '0 16px', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                  Отмена
                </button>
              </div>
              {canMax === 0 && (
                <div style={{ color: '#f59e0b', fontSize: 12, marginTop: 8 }}>
                  Добавить нечего: заказ закрыт или на складе нет остатка по этому сорту.
                </div>
              )}
            </div>
          );
        })()}

        {/* Собранные позиции */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
            📦 К отгрузке: {total} шт
          </div>
          {rows.length === 0 ? (
            <p style={{ color: 'var(--mid)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>{t('Пока ничего не добавлено')}</p>
          ) : rows.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ flex: 1, color: 'var(--text)', fontSize: 13 }}>
                {r.product} · <span style={{ color: 'var(--dim)' }}>{r.grade_name}</span>
                {r.is_defect && <DefectTag />}
              </span>
              <QtyBox value={r.qty} max={maxFor(r)} onChange={v => editQty(r, v)} />
              <button onClick={() => remove(r.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}>🗑</button>
            </div>
          ))}
        </div>

          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={shipAll} disabled={shipping || !rows.length}
            style={{ ...S.btn('#22c55e', shipping || !rows.length), flex: 1, padding: '12px 0', fontSize: 15 }}>
            {shipping ? 'Отгрузка…' : `🚛 Отгрузить (${total} шт)`}
          </button>
          <button onClick={onClose}
            style={{ ...S.btn('var(--card)'), padding: '12px 20px', color: 'var(--muted)', border: '1px solid var(--border)' }}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}

// Правка количества прямо в собранной строке.
//
// «Взять всё по заказу» набирает остаток целиком, но грузят не всегда всё: в
// машину не влезло, пачка оказалась неполной, часть отдают завтра. Раньше
// строку приходилось удалять и набирать заново — а искать позицию в списке из
// сорока дольше, чем поправить одну цифру.
function QtyBox({ value, max, onChange }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <input
        type="number" min={1} max={max}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={e => e.target.select()}
        style={{
          width: 66, padding: '4px 6px', textAlign: 'right',
          background: 'var(--input)', border: '1px solid var(--border)',
          borderRadius: 7, color: 'var(--text)', fontSize: 14, fontWeight: 700,
          fontFamily: 'inherit', outline: 'none',
        }} />
      <span style={{ color: 'var(--dim)', fontSize: 12 }}>шт</span>
    </span>
  );
}

// Метка брака. Брак отгружается — его берут дешевле, на подложку, на свои
// нужды. Но перепутать его с годным нельзя ни на одном экране: цена другая, и
// молча отдать брак вместо первого сорта — это возврат и разговор с клиентом.
function DefectTag() {
  return (
    <span style={{
      background: '#ef444422', color: '#ef4444', borderRadius: 6,
      padding: '1px 7px', fontSize: 11, fontWeight: 800,
      whiteSpace: 'nowrap', marginLeft: 6,
    }}>БРАК</span>
  );
}

// Собрать весь остаток заказа из того, что лежит на складе.
//
// Машина подъехала, пачки уже на ней — а отгрузку кладовщик набирал по одной
// позиции: найди в списке, укажи сколько, добавь, и так пятнадцать раз. При
// этом собирать нечего: что нужно — написано в заказе, что есть — известно
// складу. Кнопка складывает это сама, а человек проверяет и подтверждает.
//
// Сорт берём лучший из имеющихся: код сорта у высшего пустой, у первого «1»,
// у второго «2» — обычная сортировка строк и ставит их в этом порядке. Брака
// в списке склада нет вовсе: он отгрузке не подлежит.
function pickAllForOrder(items, stock, usedByKey, takenNow) {
  if (!items || !stock) return [];
  const used = { ...(usedByKey || {}) };
  const picked = [];
  let uid = Date.now();

  (items || []).forEach(it => {
    let need = Math.max(0, (it.quantity || 0)
      - (it.quantity_shipped || 0)
      - ((takenNow || {})[it.product] || 0));
    if (need <= 0) return;

    // Годное, лучшим сортом вперёд. Сортируем по самому сорту: высший, потом
    // первый, второй, третий — а не по коду паллеты, под которым первый и
    // третий были неразличимы.
    const avail = (stock || [])
      .filter(r => String(r.nomenclature_id) === String(it.product) && !r.is_defect)
      .slice()
      .sort((a, b) => (a.is_default ? 0 : 1) - (b.is_default ? 0 : 1)
                   || (a.grade_order || 0) - (b.grade_order || 0)
                   || String(a.grade_name || '').localeCompare(String(b.grade_name || '')));

    avail.forEach(r => {
      if (need <= 0) return;
      const key  = `${r.nomenclature_id}|${r.grade_type_id ?? r.grade}`;
      const free = Math.max(0, (r.available || 0) - (used[key] || 0));
      if (free <= 0) return;
      const take = Math.min(need, free);
      used[key] = (used[key] || 0) + take;
      need -= take;
      picked.push({
        id: uid++, key,
        nomenclature_id: r.nomenclature_id,
        product:    r.product_name,
        grade:      r.grade,
        grade_name: r.grade_name,
        is_defect:  !!r.is_defect,
        sheet_qr:   r.qr_code,
        qty:        take,
        available:  r.available,
      });
    });
  });
  return picked;
}

// ─── Отгрузка со склада (без заказа / сверх заказа) ───────────────────────────
//
// Отгружать можно было только строго по заказу и строго в его пределах. А склад
// грузит и то, чего никто не заказывал, и сверх заказа: довезли остаток, клиент
// забрал больше. Здесь обязателен только контрагент.
//
// Заказ выбирается из ОТКРЫТЫХ заказов этого же контрагента и после отгрузки
// закрывается целиком: иначе он висит и держит резерв сырья под товар, который
// никто уже не сделает, а резерв вычитается из свободного остатка.
// ─── Отгрузка по распоряжению ────────────────────────────────────────────────
//
// Раньше отсюда открывалось то же окно сборки, что и для свободной отгрузки:
// пустое, со сканером бирок. Кладовщик, у которого в руках лежит написанная
// офисом накладная, начинал собирать её заново — выбирать товар, сканировать
// листы, считать. Ровно та работа, которую офис уже сделал.
//
// Здесь он видит написанное и подтверждает. Ни листом больше, ни листом
// меньше: расхождение — это разговор с офисом, а не правка у ворот.
function NoteShipModal({ note, onClose, onDone }) {
  const [data, setData]   = useState(null);
  const [stock, setStock] = useState(null);
  const [busy, setBusy]   = useState(false);

  useEffect(() => {
    shipNoteAPI.one(note.id).then(r => setData(r.data))
      .catch(() => { toast.error(t('Распоряжение не загрузилось')); onClose(); });
    // Остаток по каждой позиции — чтобы «не хватает» было видно ДО того, как
    // кладовщик пойдёт за товаром, а не после.
    shipmentAPI.available().then(r => setStock(r.data.results || [])).catch(() => setStock([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note.id]);

  const haveOf = (nomId) => (stock || [])
    .filter(x => String(x.nomenclature_id) === String(nomId) && !x.is_defect)
    .reduce((sum, x) => sum + (x.available || 0), 0);

  const ship = async () => {
    if (!await confirmToast(
      `Отгрузить по ${data.number}: ${data.client}, всего ${data.items.reduce((s, i) => s + i.quantity, 0)} шт?`,
      { confirmText: 'Отгрузить' })) return;
    setBusy(true);
    try {
      const { data: r } = await shipmentAPI.byNote(note.id);
      toast.success(`✅ Отгружено ${r.total} шт · ${r.order_number}`);
      onDone();
    } catch (err) {
      const d = err.response?.data || {};
      if (d.short) {
        toast.error(d.error + ': ' + d.short.join('; '), { duration: 8000 });
      } else {
        toast.error(d.error || 'Ошибка отгрузки');
      }
    } finally { setBusy(false); }
  };

  if (!data) return (
    <div style={S.modalBack}><div style={{ ...S.modalBox, maxWidth: 520 }}>
      <div style={{ color: 'var(--dim)' }}>{t('Загрузка')}…</div>
    </div></div>
  );

  const total   = data.items.reduce((s, i) => s + i.quantity, 0);
  const lacking = stock !== null && data.items.some(i => haveOf(i.product_id) < i.quantity);

  return (
    <div style={S.modalBack}>
      <div style={{ ...S.modalBox, maxWidth: 720 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--text)', fontWeight: 800, fontSize: 18 }}>
              {data.number}
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 13.5, marginTop: 3 }}>
              {data.client}
              {data.order_number && <> · {t('заказ')} {data.order_number}</>}
              {data.carrier && <> · {data.carrier}</>}
              {data.vehicle && <> · {data.vehicle}</>}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--dim)',
            fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)',
                      borderRadius: 12, padding: '10px 14px', marginBottom: 14 }}>
          {data.items.map(i => {
            const have = haveOf(i.product_id);
            const ok = stock === null || have >= i.quantity;
            return (
              <div key={i.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
                borderBottom: '1px solid var(--border)', fontSize: 14,
              }}>
                <span style={{ flex: 1, minWidth: 0, color: 'var(--text)' }}>
                  {i.product}
                  {i.texture && <span style={{ color: 'var(--dim)' }}> · {i.texture}</span>}
                </span>
                {stock !== null && (
                  <span style={{ fontSize: 12.5, whiteSpace: 'nowrap',
                                 color: ok ? 'var(--dim)' : '#ef4444' }}>
                    {t('на складе')} {have}
                  </span>
                )}
                <b style={{ fontSize: 20, color: ok ? 'var(--text)' : '#ef4444',
                            minWidth: 54, textAlign: 'right' }}>{i.quantity}</b>
              </div>
            );
          })}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10,
                        paddingTop: 9, fontSize: 14 }}>
            <span style={{ flex: 1, color: 'var(--muted)', fontWeight: 700 }}>
              {t('Всего к выдаче')}
            </span>
            <b style={{ fontSize: 24, color: 'var(--text)' }}>{total}</b>
          </div>
        </div>

        {lacking && (
          <div style={{ background: '#ef444412', border: '1px solid #ef444455',
                        borderRadius: 9, padding: '10px 13px', fontSize: 13,
                        marginBottom: 14, color: 'var(--text)' }}>
            ⚠ На складе не хватает по красным строкам. Отгрузить нельзя —
            скажите офису, он поправит распоряжение.
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={ship} disabled={busy || lacking} className="touch-btn" style={{
            background: lacking ? 'var(--bg)' : '#22c55e', border: 'none',
            borderRadius: 10, color: lacking ? 'var(--mid)' : '#fff',
            padding: '13px 30px', fontSize: 16, fontWeight: 800,
            cursor: (busy || lacking) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{busy ? '…' : `✅ ${t('Отдал, подтверждаю')}`}</button>
          <span style={{ color: 'var(--dim)', fontSize: 12, flex: 1, minWidth: 180,
                         lineHeight: 1.4 }}>
            Уйдут самые старые листы этих продуктов. Бирки сканировать не нужно,
            накладную привезут из офиса.
          </span>
        </div>
      </div>
    </div>
  );
}

function FreeShipmentModal({ note: order4, onClose, onDone }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [orders, setOrders]   = useState([]);
  const [orderId, setOrderId] = useState('');
  const [code, setCode]       = useState('');
  const [found, setFound]     = useState(null);
  const [qty, setQty]         = useState('');
  const [rows, setRows]       = useState([]);
  const [note, setNote]       = useState('');
  const [busy, setBusy]       = useState(false);
  const [saving, setSaving]   = useState(false);
  // Выбрали заказ — дальше это ровно та же работа, что и отгрузка по заказу:
  // надо видеть, ЧТО собирать и сколько ещё осталось. Без состава кладовщик
  // сканирует вслепую и узнаёт о недоборе, когда машина уже гружена.
  const [items, setItems]     = useState(null);
  const [stock, setStock]     = useState(null);
  const [pick, setPick]       = useState('');
  const inputRef = useRef(null);
  const qtyRef   = useRef(null);

  useEffect(() => { salesAPI.getClients().then(r => setClients(r.data)).catch(() => {}); }, []);

  // Пришли по распоряжению — контрагент и заказ в нём уже названы. Спрашивать
  // их второй раз незачем: кладовщик держит бумагу в руках, а выбор из списка
  // — это ещё одно место, где можно промахнуться строкой.
  useEffect(() => {
    if (!order4) return;
    setClientId(String(order4.client_id));
    if (order4.order_id) setOrderId(String(order4.order_id));
  }, [order4]);

  // Состав заказа и остаток по нему. Заказ не выбран — показываем весь склад
  // ГП: отгрузить без заказа можно что угодно, но только из того, что лежит.
  useEffect(() => {
    setItems(null);
    if (orderId) {
      salesAPI.getOrder(orderId).then(r => setItems(r.data.items || [])).catch(() => setItems([]));
    }
    shipmentAPI.available(orderId ? { order: orderId } : undefined)
      .then(r => setStock(r.data.results || []))
      .catch(() => setStock([]));
  }, [orderId]);
  useEffect(() => { inputRef.current && inputRef.current.focus(); }, []);

  // Открытые заказы выбранного контрагента — их и предлагаем закрыть отгрузкой
  useEffect(() => {
    setOrderId('');
    if (!clientId) { setOrders([]); return; }
    salesAPI.getOrders({})
      .then(r => setOrders((r.data || []).filter(o =>
        String(o.client) === String(clientId) &&
        !['shipped', 'cancelled', 'closed'].includes(o.status))))
      .catch(() => setOrders([]));
  }, [clientId]);

  const focusInput = () => inputRef.current && inputRef.current.focus();

  // Собранное по продукт+сорт — чтобы не выйти за остаток склада
  const takenByKey = rows.reduce((a, r) => { a[r.key] = (a[r.key] || 0) + r.qty; return a; }, {});
  const takenByProduct = rows.reduce((a, r) => {
    a[r.nomenclature_id] = (a[r.nomenclature_id] || 0) + r.qty; return a;
  }, {});

  // Заказ выбран — значит состав известен, и собирать его руками незачем
  const takeAll = () => {
    const picked = pickAllForOrder(items, stock, takenByKey, takenByProduct);
    if (!picked.length) {
      toast.error(t('Добирать нечего: заказ собран или на складе пусто'));
      return;
    }
    setRows(prev => [...picked, ...prev]);
    const n = picked.reduce((a, r) => a + r.qty, 0);
    toast.success(`Собрано ${n} шт — проверьте и нажмите «Отгрузить»`);
  };

  const scan = async (raw) => {
    const qr = (raw || '').trim();
    if (!qr) return;
    setBusy(true);
    try {
      const { data } = await sheetAPI.lookup(qr);
      setCode('');
      if (data.is_defect) {
        toast(t('⚠ Это БРАК — отгрузить можно, но это брак'),
              { id: 'defect-scan', icon: '⚠️' });
      }
      if (!data.nomenclature_id) { toast.error(t('❌ Продукт листа не определён')); focusInput(); return; }
      setFound(data); setQty('');
      setTimeout(() => qtyRef.current && qtyRef.current.focus(), 60);
    } catch {
      setCode(''); toast.error(t('❌ Лист не найден')); focusInput();
    } finally { setBusy(false); }
  };

  const addRow = () => {
    if (!found) return;
    const n = Number(qty);
    if (!n || n <= 0) { toast.error(t('Укажите количество')); return; }
    const key = `${found.nomenclature_id}|${found.grade_type_id ?? found.grade}`;
    const inStock = found.available || 0;
    if ((takenByKey[key] || 0) + n > inStock) {
      toast.error(`На складе ${inStock} шт (${found.grade_name})`); return;
    }
    setRows(prev => [{
      id: Date.now(), key,
      nomenclature_id: found.nomenclature_id,
      product: found.product_name, grade_name: found.grade_name,
      is_defect: !!found.is_defect,
      sheet_qr: found.qr_code, qty: n, available: inStock,
    }, ...prev]);
    setFound(null); setQty(''); focusInput();
  };

  // Тут заказ может и не быть — предел один: сколько лежит на складе
  const maxFor = (row) => {
    const other = rows.filter(r => r.id !== row.id && r.key === row.key)
      .reduce((a, r) => a + r.qty, 0);
    return Math.max(0, (row.available || 0) - other);
  };
  const editQty = (row, raw) => {
    const lim = maxFor(row);
    let n = parseInt(raw, 10);
    if (!Number.isFinite(n)) n = 0;
    if (n > lim) { n = lim; toast.error(`На складе ${lim} шт`); }
    setRows(prev => prev.map(r => r.id === row.id ? { ...r, qty: Math.max(0, n) } : r));
  };

  const total = rows.reduce((s, r) => s + r.qty, 0);
  const order = orders.find(o => String(o.id) === String(orderId));

  const submit = async () => {
    if (!clientId) { toast.error(t('Укажите контрагента')); return; }
    if (!rows.length) { toast.error(t('Ничего не собрано')); return; }
    const q = order
      ? `Отгрузить ${total} шт и ЗАКРЫТЬ заказ ${order.order_number}?`
      : `Отгрузить ${total} шт без заказа?`;
    if (!await confirmToast(q, { confirmText: 'Отгрузить' })) return;
    setSaving(true);
    try {
      const { data } = await shipmentAPI.create({
        client: Number(clientId),
        order: orderId ? Number(orderId) : null,
        // Распоряжение закрывается тем же движением. Иначе оно останется
        // висеть как невыполненная работа, и назавтра по нему грузят второй раз.
        shipment_note: order4 ? order4.id : null,
        note,
        rows: rows.map(r => ({ sheet_qr: r.sheet_qr, quantity: r.qty })),
      });
      toast.success(`✅ Отгружено ${data.total} шт · документ ${data.order_number}`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка отгрузки');
    } finally { setSaving(false); }
  };

  const INP = {
    width: '100%', padding: '10px 12px', background: 'var(--input)',
    border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div className="wh-modal-wrap" style={{
      position: 'fixed', inset: 0, background: '#000000cc', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 20,
    }} onClick={onClose}>
      <div className="wh-modal" onClick={e => e.stopPropagation()} style={{
        background: 'var(--card)', borderRadius: 14, width: '100%', maxWidth: 1000,
        maxHeight: '92vh', overflowY: 'auto', padding: 22,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: 'var(--text)', fontSize: 18, flex: 1 }}>🚚 Отгрузка со склада</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--dim)', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        {/* Два столбца вместо ленты вниз. Окно набирало полтора экрана, и
            кладовщик прокручивал его туда-сюда: собранное внизу, склад
            посередине, контрагент наверху — а нужны они одновременно.
            Слева «кому», справа «что грузим». На планшете столбцы встают
            друг под друга сами. */}
        <div className="wh-two-col">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <ClientPicker label="Контрагент *" clients={clients} value={clientId} onChange={setClientId} />

          <div>
            <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>
              Заказ (необязательно)
            </label>
            <select value={orderId} onChange={e => setOrderId(e.target.value)} style={INP} disabled={!clientId}>
              <option value="">
                {!clientId ? '— сначала выберите контрагента —'
                  : orders.length ? '— без заказа —' : '— открытых заказов нет —'}
              </option>
              {orders.map(o => (
                <option key={o.id} value={o.id}>
                  {o.order_number} · {o.status_display || o.status} · {o.items_count} поз.
                </option>
              ))}
            </select>
            {order && (
              <div style={{ marginTop: 5, fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>
                ⚠ После отгрузки заказ {order.order_number} будет закрыт, резерв сырья снимется
              </div>
            )}
          </div>

          {/* Что заказано и сколько уже собрано — иначе недобор виден только
              после отгрузки, когда машина уже ушла */}
          {items && items.length > 0 && (
            <div style={{ background: 'var(--bg)', borderRadius: 10, padding: 12 }}>
              <div style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                {t('📋 СОСТАВ ЗАКАЗА')}
              </div>
              {items.map(it => {
                const taken = rows.filter(r => String(r.nomenclature_id) === String(it.product))
                  .reduce((a, r) => a + r.qty, 0) + (it.quantity_shipped || 0);
                const enough = taken >= it.quantity;
                return (
                  <div key={it.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)',
                  }}>
                    <span style={{ color: 'var(--text)', fontSize: 13.5, minWidth: 0 }}>
                      {it.product_name}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
                                   color: enough ? '#22c55e' : 'var(--muted)' }}>
                      {enough ? '✅ ' : ''}{taken} / {it.quantity} {t('шт')}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          {/* Заказ выбран — состав известен, собирать его руками незачем */}
          {items && items.length > 0 && stock !== null && stock.length > 0 && (
            <button onClick={takeAll} disabled={saving} className="touch-btn" style={{
              width: '100%', padding: '13px 0',
              background: 'var(--accent-weak)', border: '1px solid var(--accent)',
              borderRadius: 10, color: 'var(--accent)', fontSize: 15, fontWeight: 800,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>✅ {t('Взять всё по заказу')}</button>
          )}

          {/* Выбор руками. Бирка стирается, теряется и лежит в середине пачки,
              а отгрузить надо сегодня. В списке только то, что реально
              свободно: выбрать несуществующее нельзя. */}
          {stock !== null && stock.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 6 }}>
                {t('Выберите из того, что лежит на складе')}
              </label>
              <input value={pick} onChange={e => setPick(e.target.value)}
                placeholder={t('Поиск по названию…')} style={{ ...INP, marginBottom: 6 }} />
              <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex',
                            flexDirection: 'column', gap: 4 }}>
                {stock
                  .filter(r => !pick.trim()
                    || (r.product_name + ' ' + r.short_name).toLowerCase()
                         .includes(pick.trim().toLowerCase()))
                  .slice(0, 60)
                  .map(r => (
                    <button key={`${r.nomenclature_id}|${r.grade_type_id ?? r.grade}`}
                      onClick={() => {
                        setFound(r); setQty('');
                        setTimeout(() => qtyRef.current && qtyRef.current.focus(), 60);
                      }}
                      disabled={busy}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        textAlign: 'left', background: 'var(--bg)', borderRadius: 8,
                        border: '1px solid var(--border)', padding: '9px 11px',
                        cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text)',
                      }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                        {r.short_name || r.product_name}
                        <span style={{ color: 'var(--dim)', fontSize: 11.5 }}>
                          {' · '}{r.grade_name}
                        </span>
                        {r.is_defect && <DefectTag />}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {r.available} {t('шт')}
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          )}

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>
              {t('…или сканируйте бирку любого листа')}
            </label>
            <input
              ref={inputRef} value={code} disabled={busy}
              onChange={e => setCode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); scan(code); } }}
              placeholder={t('Код листа…')} style={INP}
            />
          </div>

          {found && (
            <div style={{ border: '1px solid var(--accent)', borderRadius: 10, padding: 12 }}>
              <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>{found.product_name}</div>
              <div style={{ color: 'var(--muted)', fontSize: 13, margin: '4px 0 10px' }}>
                Сорт: <b style={{ color: found.is_defect ? '#ef4444' : 'var(--text)' }}>
                  {found.grade_name}</b>{found.is_defect && <DefectTag />}
                {' · '}на складе: <b style={{ color: '#22c55e' }}>{found.available || 0} шт</b>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  ref={qtyRef} type="number" min={1} value={qty}
                  onChange={e => setQty(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRow(); } }}
                  placeholder={t('Сколько шт')} style={{ ...INP, flex: 1 }}
                />
                <button onClick={addRow} style={{
                  background: 'var(--accent)', border: 'none', borderRadius: 8, color: '#fff',
                  padding: '0 20px', fontWeight: 700, cursor: 'pointer', fontSize: 14,
                }}>+ Добавить</button>
              </div>
            </div>
          )}

          {rows.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {rows.map(r => (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg)',
                  borderRadius: 8, padding: '9px 12px',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: 'var(--text)', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.product}</div>
                    <div style={{ color: 'var(--dim)', fontSize: 11 }}>{r.grade_name}</div>
                  </div>
                  <QtyBox value={r.qty} max={maxFor(r)} onChange={v => editQty(r, v)} />
                  <button onClick={() => setRows(p => p.filter(x => x.id !== r.id))}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}>🗑</button>
                </div>
              ))}
              <div style={{ textAlign: 'right', color: 'var(--text)', fontWeight: 800, fontSize: 15 }}>
                Итого: {total} шт
              </div>
            </div>
          )}

          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
          <input value={note} onChange={e => setNote(e.target.value)}
            placeholder={t('Примечание (необязательно)')} style={INP} />

          <div style={{ display: 'flex', gap: 10, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <button onClick={submit} disabled={saving || !rows.length || !clientId} style={{
              flex: 1, background: (saving || !rows.length || !clientId) ? 'var(--bg)' : '#22c55e',
              color: (saving || !rows.length || !clientId) ? 'var(--dim)' : '#fff',
              border: 'none', borderRadius: 8, padding: '12px 0', fontSize: 15, fontWeight: 700,
              cursor: (saving || !rows.length || !clientId) ? 'not-allowed' : 'pointer',
            }}>
              {saving ? 'Отгрузка…' : `✅ Завершить отгрузку (${total} шт)`}
            </button>
            <button onClick={onClose} style={{
              background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
              color: 'var(--muted)', padding: '12px 20px', cursor: 'pointer', fontSize: 14,
            }}>Отмена</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Главный экран кладовщика ────────────────────────────────────────────────
//
// ─── Брак ДСП ────────────────────────────────────────────────────────────────
//
// Плиту бьют и режут не по сменам. Смену пресс сдаёт по нескольку раз за день,
// а битые плиты сносят в один угол и разбирают вечером, когда угол перестаёт
// быть проходом. Спрашивать про них на приёмке — значит просить кладовщика
// поделить эту кучу на смены по памяти.
//
// Поэтому здесь день: сколько плит, какая плита, и всё. Списание уходит на
// склад сразу, отдельным видом движения — в отчётах брак не смешается с
// выпуском.
function DspDefectTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate]   = useState(today);
  const [data, setData]   = useState(null);
  const [nom, setNom]     = useState('');
  const [qty, setQty]     = useState('');
  const [note, setNote]   = useState('');
  const [busy, setBusy]   = useState(false);

  const load = useCallback(() => {
    dspDefectAPI.list(date)
      .then(r => {
        setData(r.data);
        // Подставляем плиту, которой сегодня работали: чаще всего бьётся она,
        // и выбирать из тысячи позиций незачем.
        setNom(prev => prev || String((r.data.choices || [])[0]?.id || ''));
      })
      .catch(() => toast.error(t('Не загрузилось')));
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const n = Number(qty || 0);
    if (!nom) { toast.error(t('Выберите плиту')); return; }
    if (n <= 0) { toast.error(t('Укажите количество')); return; }
    const pick = (data.choices || []).find(c => String(c.id) === String(nom));
    if (!await confirmToast(
      `Списать со склада ${n} ${pick?.unit || 'шт'} — ${pick?.name}?`,
      { confirmText: 'Списать' })) return;
    setBusy(true);
    try {
      await dspDefectAPI.add({ date, nomenclature_id: nom, quantity: n, note });
      setQty(''); setNote('');
      toast.success(t('Списано'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setBusy(false); }
  };

  const undo = async (row) => {
    if (!await confirmToast(`Отменить: ${row.name} ${-row.quantity}?`,
                            { confirmText: 'Отменить' })) return;
    try {
      await dspDefectAPI.undo(row.id);
      toast.success(t('Отменено'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    }
  };

  if (!data) return <div style={{ color: 'var(--dim)' }}>{t('Загрузка')}…</div>;

  const BOX = { background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 12, padding: '14px 16px' };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--muted)', fontSize: 13 }}>{t('День')}</span>
        <input type="date" value={date} max={today}
          onChange={e => setDate(e.target.value)}
          style={{ ...S.input, width: 168 }} />
        <span style={{ marginLeft: 'auto', color: 'var(--muted)', fontSize: 13 }}>
          {t('за день списано')}{' '}
          <b style={{ color: data.total ? '#ef4444' : 'var(--text)', fontSize: 20 }}>
            {data.total}
          </b>
        </span>
      </div>

      <div style={BOX}>
        <div style={{ color: 'var(--dim)', fontSize: 11, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '.06em',
                      marginBottom: 10 }}>
          {t('Списать брак')}
        </div>

        {/* Первой строкой — плита, которой в этот день работали: чаще всего
            бьётся именно она. Остальной справочник ниже, но только то, что
            есть на остатке: списывать с нуля нечего. */}
        <div style={{ display: 'grid', gap: 10,
                      gridTemplateColumns: 'minmax(220px, 2fr) 110px minmax(160px, 1fr) auto',
                      alignItems: 'end' }}>
          <div>
            <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block',
                            marginBottom: 4 }}>{t('Какая плита')}</label>
            <select value={nom} onChange={e => setNom(e.target.value)}
              style={{ ...S.input, width: '100%' }}>
              <option value="">— {t('выберите')} —</option>
              {(data.choices || []).map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.used_today > 0 ? ` · сегодня в работе ${c.used_today}` : ''}
                  {` · остаток ${c.balance}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block',
                            marginBottom: 4 }}>{t('Сколько')}</label>
            <input type="text" inputMode="numeric" value={qty}
              onChange={e => setQty(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
              onFocus={e => e.target.select()} placeholder="0"
              style={{ ...S.input, width: '100%', textAlign: 'center',
                       fontWeight: 800, fontSize: 18 }} />
          </div>
          <div>
            <label style={{ color: 'var(--muted)', fontSize: 12, display: 'block',
                            marginBottom: 4 }}>{t('Примечание')}</label>
            <input value={note} onChange={e => setNote(e.target.value)}
              placeholder={t('не обязательно')} style={{ ...S.input, width: '100%' }} />
          </div>
          <button onClick={add} disabled={busy} className="touch-btn" style={{
            background: '#ef4444', border: 'none', borderRadius: 9, color: '#fff',
            padding: '11px 22px', fontSize: 14, fontWeight: 800,
            cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            whiteSpace: 'nowrap',
          }}>{busy ? '…' : t('Списать')}</button>
        </div>

        <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
          Плита уйдёт с остатка сразу. Ошиблись — строку ниже можно отменить:
          она не стирается, а гасится встречной, и в 1С расхождение видно.
        </div>
      </div>

      <div style={BOX}>
        <div style={{ color: 'var(--dim)', fontSize: 11, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '.06em',
                      marginBottom: 10 }}>
          {t('Списано в этот день')}
        </div>
        {(data.rows || []).length === 0 && (
          <div style={{ color: 'var(--dim)', fontSize: 13 }}>{t('Пока ничего')}</div>
        )}
        {(data.rows || []).map(r => (
          <div key={r.id} style={{
            display: 'flex', alignItems: 'center', gap: 12, fontSize: 13.5,
            padding: '7px 0', borderBottom: '1px solid var(--border)',
          }}>
            <b style={{ color: r.quantity < 0 ? '#ef4444' : '#22c55e',
                        width: 62, textAlign: 'right', fontSize: 16 }}>
              {r.quantity < 0 ? -r.quantity : '+' + r.quantity}
            </b>
            <span style={{ flex: 1, minWidth: 0, color: 'var(--text)' }}>
              {r.name}
              {r.note && <span style={{ color: 'var(--dim)' }}> · {r.note}</span>}
            </span>
            <span style={{ color: 'var(--dim)', fontSize: 12, whiteSpace: 'nowrap' }}>
              {r.who} · {new Date(r.at).toLocaleTimeString('ru-RU',
                        { hour: '2-digit', minute: '2-digit' })}
            </span>
            {r.quantity < 0 && (
              <button onClick={() => undo(r)} style={{
                background: 'none', border: '1px solid var(--border)', borderRadius: 7,
                color: 'var(--dim)', padding: '4px 10px', fontSize: 12,
                cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
              }}>{t('отменить')}</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// В смену кладовщик делает четыре вещи: принимает листы от пресса, правит сорта,
// отгружает и в конце дня списывает битые плиты. Всё остальное — справочно, и открывают его изредка. Но лежало это
// вперемешку в боковом меню, а на планшете меню ещё и выезжает: до работы было
// два касания, и оба — по мелким строкам списка.
//
// Здесь три дела крупно и сразу, с числом ожидающего: сколько смен сдал пресс и
// сколько заказов готово к отгрузке. Закончил — одна кнопка возвращает сюда.
// Остаток одним экраном. Вопрос у кладовщика один — «сколько чего лежит», — а
// дверей было три: «Сырьё», «По складам», «Готовая продукция». Две из трёх шли
// с одинаковым значком 📦, и какая из них ответит, по названию было не понять:
// человек открывал первую, не находил и шёл во вторую.
//
// Здесь взгляд переключается на месте: все три подписаны рядом, видно сразу,
// что вообще можно посмотреть, и переход не стоит возврата назад.
function StockLook({ refresh, onRefresh }) {
  const [look, setLook] = useState('raw');
  const LOOKS = [
    { key: 'raw',  label: t('Сырьё'),             hint: t('плёнка, ДСП, бумага') },
    { key: 'gp',   label: t('Готовая продукция'), hint: t('ЛДСП по сортам') },
    { key: 'by_wh', label: t('По складам'),       hint: t('что где лежит') },
  ];
  return (
    <>
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14,
      }}>
        {LOOKS.map(x => (
          <button key={x.key} onClick={() => setLook(x.key)} className="touch-btn"
            title={x.hint} style={{
              background: look === x.key ? 'var(--accent)' : 'var(--card)',
              border: '1px solid ' + (look === x.key ? 'var(--accent)' : 'var(--border)'),
              borderRadius: 10, color: look === x.key ? '#fff' : 'var(--muted)',
              padding: '9px 18px', fontSize: 14, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>{x.label}</button>
        ))}
      </div>
      {look === 'raw'   && <StockSummary refresh={refresh} onRefresh={onRefresh} />}
      {look === 'gp'    && <FinishedStock refresh={refresh} />}
      {look === 'by_wh' && <StockByWarehouse />}
    </>
  );
}

function HomeScreen({ counts, onGo }) {
  const TILES = [
    { key: 'acceptance', icon: '🏭', title: 'Приёмка из цеха',
      hint: 'Пресс сдал смену — принять листы и проставить сорта',
      count: counts.acceptance, color: 'var(--accent)' },
    // «Сорта» — так назывался и справочник сортов у технолога, и разрез у
    // директора. Здесь это не список, а действие: лист лежит не в том сорте,
    // его переводят. Название должно говорить, что тут ДЕЛАЮТ.
    { key: 'regrade', icon: '🏷', title: 'Изменить сорт',
      hint: 'Перевести листы в другой сорт — терминал на одну руку',
      count: 0, color: '#8b5cf6' },
    // «Отгрузка» была у трёх ролей сразу, и означала три разные вещи: технолог
    // ВЫПИСЫВАЕТ распоряжение, менеджер тоже выписывает, а склад по нему
    // ВЫДАЁТ. Одно слово на три работы — и кладовщик заходил туда, где ничего
    // для него не было. Здесь оставляем ровно свою: выдать.
    { key: 'ship', icon: '🚚', title: 'Выдать клиенту',
      hint: 'Собрать по распоряжению офиса и отдать',
      count: counts.ship, color: '#f59e0b' },
    // Четвёртое дело, и делают его раз в день, а не по сменам: битые плиты
    // за смену не считают — их относят в один угол и разбирают вечером.
    { key: 'dspdefect', icon: '⚠️', title: 'Брак ДСП',
      hint: 'Собрали за день — списать разом',
      count: 0, color: '#ef4444' },
  ];

  return (
    <div style={{
      display: 'grid', gap: 14,
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    }}>
      {TILES.map(x => (
        <button key={x.key} onClick={() => onGo(x.key)} className="touch-btn" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
          gap: 8, textAlign: 'left', width: '100%', minHeight: 156,
          background: 'var(--card)', border: '1px solid var(--border)',
          borderLeft: `5px solid ${x.color}`, borderRadius: 16,
          padding: '20px 22px', cursor: 'pointer', fontFamily: 'inherit',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
            <span style={{ fontSize: 34, lineHeight: 1 }}>{x.icon}</span>
            {/* Число ждущего — единственное, ради чего сюда смотрят издалека:
                видно, есть ли работа, ещё не подойдя к планшету */}
            {x.count > 0 && (
              <span style={{
                marginLeft: 'auto', background: x.color, color: '#fff',
                borderRadius: 22, minWidth: 44, padding: '4px 14px',
                fontSize: 20, fontWeight: 800, textAlign: 'center',
              }}>{x.count}</span>
            )}
          </div>
          <div style={{ color: 'var(--text)', fontSize: 21, fontWeight: 800 }}>
            {x.title}
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 13.5, lineHeight: 1.45 }}>
            {x.hint}
          </div>
        </button>
      ))}
    </div>
  );
}

// Возврат к трём делам — крупной строкой сверху, а не стрелкой в углу: планшет
// держат одной рукой, и промахнуться по мелкой кнопке проще, чем попасть.
//
// Сюда же встают вкладки раздела, если они есть. Отдельной полосой под шапкой
// они съедали ещё строку — на телефоне это заметная часть экрана, а работа
// начинается ниже. Название раздела при этом убираем: вкладки называют его
// точнее.
function BackHome({ onGo, title, tabs, tab, onTab }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '8px 12px', marginBottom: 12,
    }}>
      <button onClick={() => onGo('home')} className="touch-btn" style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'none', border: 'none', padding: '4px 4px',
        cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text)',
      }}>
        <span style={{ fontSize: 18 }}>←</span>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{t('К трём делам')}</span>
      </button>

      {(tabs || []).length > 0 ? (
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap',
                       marginLeft: 'auto' }}>
          {tabs.map(x => (
            <button key={x.key} onClick={() => onTab(x.key)} className="touch-btn"
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                background: tab === x.key ? 'var(--accent)' : 'transparent',
                color: tab === x.key ? '#fff' : 'var(--muted)',
                border: '1px solid ' + (tab === x.key ? 'var(--accent)' : 'var(--border)'),
                borderRadius: 9, padding: '7px 14px', fontSize: 13.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
              {x.label}
              <span style={{
                background: tab === x.key ? 'rgba(255,255,255,.25)' : 'var(--bg)',
                color: tab === x.key ? '#fff' : 'var(--dim)',
                borderRadius: 20, padding: '0 8px', fontSize: 12, fontWeight: 800,
              }}>{x.count}</span>
            </button>
          ))}
        </span>
      ) : (
        <span style={{ marginLeft: 'auto', color: 'var(--dim)', fontSize: 13.5 }}>
          {title}
        </span>
      )}
    </div>
  );
}

// Откуда вели работу. Моноблок у пресса и браузер в кабинете ходят по одному
// адресу, и по строке их было не отличить — а выпуск, отмеченный из кабинета,
// у станка никто не считал. Значок молчит, когда всё как обычно: подсвечиваем
// только необычное, иначе он превращается в фон.
function SourceTag({ source, runs }) {
  const web = source === 'web';
  if (!web && !(runs > 1)) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
      {web && (
        <span title="Работу вели из браузера, а не с моноблока у пресса" style={{
          background: '#f59e0b1f', color: '#b45309', border: '1px solid #f59e0b55',
          borderRadius: 6, padding: '1px 7px', fontSize: 11.5, fontWeight: 700,
          whiteSpace: 'nowrap',
        }}>🖥 из браузера</span>
      )}
      {runs > 1 && (
        <span title="Оператор возвращался к позиции несколько раз" style={{
          background: 'var(--bg)', color: 'var(--dim)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '1px 7px', fontSize: 11.5, whiteSpace: 'nowrap',
        }}>заходов: {runs}</span>
      )}
    </span>
  );
}

// ─── Отгрузка ────────────────────────────────────────────────────────────────
//
// Здесь лежала вся очередь готовых заказов, и кладовщик сам решал, какой из
// них грузить и сколько. На сорока строках это решение принимается наугад, а
// ошибка стоит машины, уехавшей не к тому клиенту.
//
// Теперь у склада один источник работы — распоряжение офиса. Что везти и
// сколько, решает главный менеджер; кладовщик выносит и подтверждает. Очередь
// заказов и свободная отгрузка отсюда убраны: два разных способа отдать товар
// означали два разных ответа на вопрос «сколько на складе», и сходились они
// только к вечеру.
//
// Машина пришла без документа — офис выписывает распоряжение за минуту, в том
// числе и без заказа. Это то же самое действие, но след от него остаётся.
function ShipOrdersTab({ onShipped, onGo }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shipNote, setShipNote] = useState(null);
  // Отгруженное — чтобы было чем свериться в конце дня: что уехало, к кому и
  // когда. Период спрашиваем, но не требуем: обычно смотрят последнее, и
  // тогда список просто идёт от свежего к старому.
  const [done, setDone]     = useState([]);
  const [from, setFrom]     = useState('');
  const [to, setTo]         = useState('');
  // Состав отгруженного подтягиваем по клику: «а что именно уехало» спрашивают
  // не про каждую строку, а про одну — грузить весь список ради этого незачем.
  const [openDone, setOpenDone] = useState(null);
  // Две разные работы на одном экране мешали друг другу: распоряжения — это
  // то, что надо выдать сейчас, а отгруженное — справка «а Камбаровой
  // отдали?». Одно листают до конца, во второе заглядывают.
  const [tab, setTab] = useState('wait');
  // Распоряжений за день набирается десяток, отгруженных — сотня. Спрашивают
  // про одно: «а Камбаровой отдали?» — и ищут его глазами по всему списку.
  const [q, setQ] = useState('');
  const [doneItems, setDoneItems] = useState({});
  const { notify } = useNotifications();
  const seen = useRef(null);

  const loadNotes = useCallback(() => {
    shipNoteAPI.list({ status: 'issued' })
      .then(r => {
        const data = r.data.results || [];
        setNotes(data);
        // Офис выписал — кладовщик узнаёт об этом сам, не заглядывая в экран
        const ids = data.map(n => n.id);
        if (seen.current === null) {
          seen.current = new Set(ids);
        } else {
          const fresh = data.filter(n => !seen.current.has(n.id));
          if (fresh.length) {
            seen.current = new Set(ids);
            notify(`🚚 Новое распоряжение (${fresh.length})`,
                   fresh.map(n => `${n.number} — ${n.client}`).join(', '));
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [notify]);

  useEffect(() => {
    loadNotes();
    const tm = setInterval(loadNotes, 30000);
    return () => clearInterval(tm);
  }, [loadNotes]);

  const toggleDone = (n) => {
    if (openDone === n.id) { setOpenDone(null); return; }
    setOpenDone(n.id);
    if (!doneItems[n.id]) {
      shipNoteAPI.one(n.id)
        .then(r => setDoneItems(c => ({ ...c, [n.id]: r.data.items || [] })))
        .catch(() => toast.error(t('Состав не загрузился')));
    }
  };

  const loadDone = useCallback(() => {
    const p = { status: 'done', limit: 100 };
    if (from) p.from = from;
    if (to) p.to = to;
    shipNoteAPI.list(p).then(r => setDone(r.data.results || [])).catch(() => {});
  }, [from, to]);
  useEffect(() => { loadDone(); }, [loadDone]);

  return (
    <div style={{ marginTop: 8 }}>
      {shipNote && (
        <NoteShipModal
          note={shipNote}
          onClose={() => setShipNote(null)}
          onDone={() => { setShipNote(null); loadNotes(); loadDone();
                          if (onShipped) onShipped(); }}
        />
      )}

      {/* Вкладки стоят в верхней строке — отдельной полосой они съедали ещё
          одну, а на телефоне работа начинается ниже. */}
      <BackHome onGo={onGo} title={t('Отгрузка')} tab={tab} onTab={setTab}
        tabs={[
          { key: 'wait', label: t('К отгрузке'), count: notes.length },
          { key: 'done', label: t('Отгружено'), count: done.length },
        ]} />

      <input value={q} onChange={e => setQ(e.target.value)}
        placeholder={t('Клиент, номер, заказ…')}
        style={{ ...S.input, marginBottom: 12 }} />

      {tab !== 'wait' ? null : loading ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 20 }}>
          {t('Загрузка…')}
        </p>
      ) : notes.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', padding: '34px 20px' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>📄</div>
          <p style={{ color: 'var(--muted)', fontSize: 16, fontWeight: 600, margin: 0 }}>
            {t('Грузить нечего')}
          </p>
          <p style={{ color: 'var(--dim)', fontSize: 13.5, marginTop: 8, lineHeight: 1.5 }}>
            Отгрузка начинается с распоряжения офиса. Машина пришла, а документа
            нет — позвоните главному менеджеру, он выпишет за минуту.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {notes.filter(n => makeMatcher(q)(
              n.number, n.client, n.order_number, n.carrier, n.vehicle)).map(n => (
            <div key={n.id} style={{
              ...S.card, borderLeft: '3px solid #f59e0b', padding: '12px 15px',
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <span style={{ flex: '1 1 250px', minWidth: 0, fontSize: 14.5 }}>
                <b style={{ color: 'var(--text)' }}>{n.number}</b>
                <span style={{ color: 'var(--text)' }}> · {n.client}</span>
                {n.order_number && (
                  <span style={{ color: '#f59e0b' }}> · {n.order_number}</span>
                )}
                <span style={{ display: 'block', color: 'var(--dim)',
                               fontSize: 11.5, marginTop: 2 }}>
                  {n.created_by} · {new Date(n.created_at).toLocaleString('ru-RU')}
                  {n.carrier && ' · ' + n.carrier}
                  {n.vehicle && ' · ' + n.vehicle}
                </span>
              </span>
              <span style={{ color: 'var(--muted)', fontSize: 13.5, whiteSpace: 'nowrap' }}>
                {t('выдать')}{' '}
                <b style={{ color: 'var(--text)', fontSize: 19 }}>{n.total}</b>
              </span>
              <button onClick={() => setShipNote(n)} className="touch-btn" style={{
                background: '#22c55e', border: 'none', borderRadius: 9, color: '#fff',
                padding: '11px 22px', fontSize: 14.5, fontWeight: 800,
                cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
              }}>🚚 {t('Отгрузить')}</button>
            </div>
          ))}
        </div>
      )}

      {/* Уже отгружено. Кладовщик сверяется с этим списком в конце дня и
          когда звонит офис: «а Камбаровой отдали?» — ответ должен быть под
          рукой, а не в чужом разделе. */}
      <div style={{ display: tab === 'done' ? 'block' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                      marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--dim)', fontSize: 12.5 }}>{t('с')}</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ ...S.input, width: 150, padding: '6px 9px', fontSize: 13 }} />
          <span style={{ color: 'var(--dim)', fontSize: 12.5 }}>{t('по')}</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ ...S.input, width: 150, padding: '6px 9px', fontSize: 13 }} />
          {(from || to) && (
            <button onClick={() => { setFrom(''); setTo(''); }} style={{
              background: 'none', border: 'none', color: 'var(--dim)',
              cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' }}>
              {t('за всё время')}
            </button>
          )}
          <span style={{ marginLeft: 'auto', color: 'var(--dim)', fontSize: 12.5 }}>
            {done.length} {t('шт')} · {done.reduce((a, n) => a + (n.total || 0), 0)} {t('листов')}
          </span>
        </div>

        {done.length === 0 ? (
          <div style={{ color: 'var(--dim)', fontSize: 13, padding: '10px 2px' }}>
            {(from || to) ? t('За этот период ничего не отгружали')
                          : t('Отгрузок пока не было')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {done.filter(n => makeMatcher(q)(
                n.number, n.client, n.order_number, n.carrier, n.vehicle)).map(n => {
              const open = openDone === n.id;
              const items = doneItems[n.id];
              return (
                <div key={n.id} style={{
                  ...S.card, borderLeft: '3px solid #22c55e', padding: '8px 13px',
                  fontSize: 13,
                  // Раскрытая строка отходит от соседей: иначе её состав
                  // читается как продолжение следующей
                  ...openCard(open, '#22c55e'),
                }}>
                  <button onClick={() => toggleDone(n)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    width: '100%', background: 'none', border: 'none', padding: 0,
                    cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                    textAlign: 'left', color: 'var(--text)',
                  }}>
                    <span style={{ color: 'var(--dim)', width: 12, flexShrink: 0 }}>
                      {open ? '▾' : '▸'}
                    </span>
                    <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                      <b style={{ color: 'var(--text)' }}>{n.number}</b>
                      <span style={{ color: 'var(--text)' }}> · {n.client}</span>
                      {n.order_number && (
                        <span style={{ color: 'var(--dim)' }}> · {n.order_number}</span>
                      )}
                    </span>
                    <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      <b style={{ color: 'var(--text)', fontSize: 15 }}>{n.total}</b> {t('шт')}
                    </span>
                    <span style={{ color: 'var(--dim)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {n.done_at
                        ? new Date(n.done_at).toLocaleString('ru-RU',
                            { day: '2-digit', month: '2-digit', year: '2-digit',
                              hour: '2-digit', minute: '2-digit' })
                        : ''}
                    </span>
                  </button>

                  {open && (
                    <div style={openDivider('#22c55e')}>
                      {!items && (
                        <div style={{ color: 'var(--dim)', fontSize: 12.5 }}>
                          {t('Загрузка')}…
                        </div>
                      )}
                      {(items || []).map(i => (
                        <div key={i.id} style={{
                          display: 'flex', gap: 10, padding: '3px 0', fontSize: 12.5,
                        }}>
                          <span style={{ flex: 1, minWidth: 0, color: 'var(--muted)' }}>
                            {i.product}
                            {i.texture && <span style={{ color: 'var(--dim)' }}> · {i.texture}</span>}
                          </span>
                          <b style={{ color: 'var(--text)' }}>{i.quantity_shipped || i.quantity}</b>
                        </div>
                      ))}
                      {(n.carrier || n.vehicle) && (
                        <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 6 }}>
                          {[n.carrier, n.vehicle].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ТАБ 2 — ЛДСП ЛАР (тайёр махсулот омбор қолдиғи)
// ═══════════════════════════════════════════════════════════════════
function FinishedStock({ refresh }) {
  const [stock, setStock]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [collapsed, setCollapsed] = useState(false);

  const load = () => {
    setLoading(true);
    warehouseAPI.getStockSummary({ type: 'finished' })
      .then(r => setStock(r.data))
      .catch(() => toast.error(t("Не удалось загрузить остатки ЛДСП")))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [refresh]); // eslint-disable-line

  const filtered = search.trim()
    ? stock.filter(it => makeMatcher(search)(it.name, it.grade_name, it.texture_name))
    : stock;

  return (
    <div style={S.card}>
      {/* Шапка держится наверху: в списке шестьсот позиций, и поиск —
          единственный способ дойти до нужной. Уезжая вместе со списком, он
          заставлял прокручивать обратно к началу ради каждого запроса. */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: collapsed ? 0 : 16, cursor: 'pointer', userSelect: 'none',
          position: 'sticky', top: 0, zIndex: 6, background: 'var(--card)',
          padding: '14px 0 10px', margin: '-14px 0 6px',
        }}
      >
        <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 16 }}>📦 ЛДСП — склад готовой продукции ({stock.length})</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
          <input
            type="text"
            placeholder={t("🔍 Поиск...")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onClick={e => e.stopPropagation()}
            style={{
              width: 160, padding: '6px 10px', background: 'var(--bg)',
              border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text)',
              fontSize: 12, outline: 'none',
            }}
          />
          <button onClick={(e) => { e.stopPropagation(); load(); }} style={{
            background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--dim)', padding: '6px 12px', cursor: 'pointer',
          }}>🔄</button>
          <span style={{ color: 'var(--dim)', fontSize: 16, transition: 'transform 0.2s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▾</span>
        </div>
      </div>
      {!collapsed && (loading ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 20 }}>Загрузка...</p>
      ) : filtered.length === 0 ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 20 }}>{search.trim() ? 'Ничего не найдено' : 'Остатков ЛДСП нет.'}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(item => {
            const pct = item.total_quantity > 0
              ? Math.round((item.available_quantity / item.total_quantity) * 100) : 0;
            const barColor = pct > 50 ? '#22c55e' : pct > 20 ? '#f59e0b' : '#ef4444';
            return (
              <div key={item.id} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                  <span style={{ color: 'var(--text)', fontSize: 14 }}>{item.name}</span>
                  <div style={{ display: 'flex', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--mid)' }}>Всего: <b style={{ color: 'var(--muted)' }}>{item.total_quantity} {item.unit}</b></span>
                    <span style={{ color: 'var(--mid)' }}>Свободно: <b style={{ color: barColor }}>{item.available_quantity} {item.unit}</b></span>
                  </div>
                </div>
                <div style={{ background: 'var(--card)', borderRadius: 4, height: 4 }}>
                  <div style={{ width: `${pct}%`, height: 4, background: barColor, borderRadius: 4 }} />
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
function RegradeTab() {
  const nav = useNavigate();
  return (
    <div>
      <button onClick={() => nav('/sheet-terminal')} className="touch-btn" style={{
        display: 'flex', alignItems: 'center', gap: 14, width: '100%',
        background: 'var(--accent)', border: 'none', borderRadius: 14, color: '#fff',
        padding: '18px 22px', marginBottom: 16, cursor: 'pointer',
        fontFamily: 'inherit', textAlign: 'left',
      }}>
        <span style={{ fontSize: 30 }}>🏷</span>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'block', fontSize: 19, fontWeight: 800 }}>Терминал пересортицы</span>
          <span style={{ display: 'block', fontSize: 13, opacity: .9, marginTop: 2 }}>
            Один лист за раз, крупные кнопки сортов, история последних операций
          </span>
        </span>
        <span style={{ fontSize: 24 }}>→</span>
      </button>
      <div style={{ ...S.card, color: 'var(--muted)', fontSize: 13, lineHeight: 1.6 }}>
        Сорт листа меняют здесь. Остаток склада считается по листам:
        сколько принято и не отгружено, столько и можно отдать.
      </div>
    </div>
  );
}

// Отказы, которые не про поломку, а про порядок вещей: бирка настоящая и
// прочиталась, просто доски за ней нет. Красный цвет тут врёт — приёмщик
// несёт такую бирку начальнику как сбой программы.
const SOFT_REJECT = ['no_sheet', 'voided'];

function AcceptanceModal({ jobId, onClose, onDone }) {
  const [data, setData]     = useState(null);
  const [grades, setGrades] = useState([]);      // справочник сортов листа
  const [marks, setMarks]   = useState({});      // sheet_id -> grade_type_id
  const [mats, setMats]     = useState({});      // grade_type_id -> число
  // Сорта числом — когда бирок в цеху нет вовсе. Приёмщик считает листы
  // глазами и пишет, сколько какого сорта; какой именно лист второго сорта,
  // не знает никто, и знать неоткуда.
  const [gcnt, setGcnt]     = useState({});      // grade_type_id -> число
  // Причина к каждому сорту. Не выбрана — так и уйдёт: приёмщик стоит у
  // стопки, за ним машина, и запирать приёмку из-за кнопки значит получить
  // причину, выбранную наугад. Пустая графа честнее.
  const [greason, setGreason] = useState({});   // grade_type_id -> reason_id
  const [reasons, setReasons] = useState([]);
  // Правка выпуска приёмщиком: ночная смена ушла домой, а стопка не сходится.
  const [fixOpen, setFixOpen] = useState(false);
  const [fixQty, setFixQty]   = useState('');
  const [fixing, setFixing]   = useState(false);
  // Склад насчитал больше, чем в MES: лишние листы заводятся при приёмке.
  // Храним только превышение — уменьшать приёмку здесь по-прежнему нельзя.
  const [extra, setExtra]   = useState(0);
  const [code, setCode]     = useState('');
  const [found, setFound]   = useState(null);    // отсканированный лист
  const [reject, setReject] = useState(null);    // чужая или негодная бирка
  // Хуки — только здесь, до любого return. Ниже по функции стоит выход
  // «данные ещё не пришли», и хук, объявленный после него, вызывается не
  // на каждом рендере: React считает их по порядку и падает целиком —
  // «Rendered more hooks than during the previous render», красный экран
  // вместо приёмки.
  const [labelCode, setLabelCode] = useState('');

  useEffect(() => {
    sheetAPI.defectReasons().then(r => setReasons(r.data || [])).catch(() => {});
  }, []);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Приёмщик стоит у стопки со сканером: пока он перебирает листы, набранное
  // живёт только в этом окне. Одно неверное касание по тёмному полю вокруг —
  // и окно закрывалось, а вместе с ним пропадали и отмеченные сорта, и
  // пересчитанный брак. На планшете мимо кнопки попадают постоянно.
  //
  // Поэтому черновик пишется в память браузера на каждое изменение и при
  // открытии той же смены возвращается. Ключ — по смене: у соседней свой.
  const draftKey = 'lamtech_accept_' + jobId;

  // onClose держим в ссылке, а не в зависимостях эффекта: смену читаем РОВНО
  // один раз, на открытие окна. Стоит эффекту повториться — setMats затрёт
  // пересчитанный брак, а приёмщик этого даже не заметит: цифры просто
  // вернутся к тому, что отметил пресс.
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  useEffect(() => {
    Promise.all([
      sheetAPI.acceptanceJob(jobId),
      gradeAPI.getGradeTypes({ active: 'true', scope: 'sheet' }),
    ]).then(([d, g]) => {
      setData(d.data);
      setGrades(g.data || []);
      const m = {};
      (d.data.materials || []).forEach(x => { m[x.grade_type_id] = x.quantity; });
      // Черновик важнее того, что прислал сервер: он новее. Сервер знает
      // отметки пресса, а приёмщик уже начал их править.
      let draft = null;
      try { draft = JSON.parse(sessionStorage.getItem(draftKey) || 'null'); }
      catch (e) { draft = null; }
      setMats(draft && draft.mats ? draft.mats : m);
      if (draft && draft.gcnt) setGcnt(draft.gcnt);
      if (draft && draft.marks) setMarks(draft.marks);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 80);
    }).catch(() => { toast.error(t('Смена не загрузилась')); closeRef.current(); });
  }, [jobId, draftKey]);

  // Пишем на каждое изменение, а не при закрытии: закрытие может и не
  // случиться — вкладку закрывают, планшет гасит экран, браузер выгружает
  // страницу. Сохранённое к этому моменту уже лежит.
  useEffect(() => {
    try {
      sessionStorage.setItem(draftKey, JSON.stringify({ marks, mats, gcnt }));
    } catch (e) {}
  }, [draftKey, marks, mats, gcnt]);

  if (!data) {
    return (
      <div style={S.modalBack}>
        <div style={{ ...S.modalBox, maxWidth: 520 }}>
          <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 30 }}>Загрузка…</p>
        </div>
      </div>
    );
  }

  // Чей это лист — решает сервер. Приёмщик стоит у стопки и бьёт сканером
  // подряд; рядом лежат стопки других смен, и чужая бирка попадает под
  // сканер сама собой. Принять её молча — значит записать брак на чужую
  // работу, и виноватым окажется тот, кто ни при чём.
  const scan = async (raw) => {
    // Сканер читает QR с бирки, а в нём напечатана ссылка целиком:
    // HTTPS://LAMTECH.MESPRO.UZ/S/U9DPJ1U. Приёмщику знать этого не нужно —
    // берём из неё последнюю часть, как если бы он ввёл код руками.
    let v = (raw || '').trim();
    const slash = v.lastIndexOf('/');
    if (slash >= 0 && /^https?:\/\//i.test(v)) v = v.slice(slash + 1);
    setCode('');
    if (!v) return;
    setFound(null); setReject(null);
    try {
      const { data: r } = await sheetAPI.acceptanceScan(jobId, v);
      // Бирку могли выводить из счёта, а её принесли — счёт поправился сам.
      // Приёмщику про это знать нечего: он поднёс бирку и получил лист.
      if (r.ok) { setFound(r.sheet); return; }
      setReject(r);                       // держим на экране до следующего скана
    } catch (err) {
      setReject({ reason: 'error', message: errText(err, 'Не удалось прочитать бирку') });
    } finally {
      inputRef.current && inputRef.current.focus();
    }
  };

  const assign = (gradeId) => {
    if (!found) return;
    setMarks(p => ({ ...p, [found.id]: gradeId }));
    setFound(null);
    inputRef.current && inputRef.current.focus();
  };

  const unmark = (sheetId) => setMarks(p => {
    const n = { ...p }; delete n[sheetId]; return n;
  });

  // Что получится после приёмки: сорт с листа, если приёмщик его не менял
  const finalOf = (sh) => marks[sh.id] || sh.grade_type_id;
  const tally = {};
  data.sheets.forEach(sh => {
    const g = finalOf(sh);
    tally[g] = (tally[g] || 0) + 1;
  });
  const gradeName = (id) => (grades.find(g => String(g.id) === String(id)) || {}).name || '—';
  const opMap = {};
  (data.operator_grades || []).forEach(g => { opMap[g.grade_type_id] = g.quantity; });

  // Плёнку считает приёмщик — она ушла в пресс вместе со сменой. ДСП живёт
  // отдельным экраном: его брак собирают за день, а не за смену.
  const filmMaterials = (data.materials || []).filter(m => {
    const n = (m.name || '').toLowerCase();
    return n.includes('плён') || n.includes('плен') || n.includes('бумаг');
  });

  // Бирок нет — сорта ставим числом. Высший сорт не спрашиваем: он и есть
  // остаток, иначе приёмщик складывал бы в уме то, что и так известно.
  const noLabel = !!data.without_label;
  // Оператор у пресса сорт не ставит: он считает листы, вышедшие не
  // высшими, а какой это сорт — первый, второй или брак — решает склад.
  // Пока его счётчик стоял отдельной строкой среди сортов, приёмщик видел
  // «Брак — пресс: 2», а число вписывал в «1-й сорт»: подсказка оказывалась
  // не на той строке, и приёмка каждый раз спрашивала, точно ли он не
  // согласен с прессом.
  const nlGrades = grades.filter(g => !g.is_default && !g.is_press_hint);
  const hintGrade = grades.find(g => g.is_press_hint);
  const hintN = hintGrade ? (opMap[hintGrade.id] || 0) : 0;
  const nlNum = (id) => Math.max(0, parseInt(gcnt[id], 10) || 0);
  const nlUsed = nlGrades.reduce((a, g) => a + nlNum(g.id), 0);
  const nlRest = data.pending + extra - nlUsed;

  // Сколько не высших получилось у склада — по всем сортам сразу: именно
  // это число сравнимо с одним числом пресса.
  const nlTotal = noLabel
    ? nlGrades.reduce((a, g) => a + nlNum(g.id), 0)
    : grades.filter(g => !g.is_default && !g.is_press_hint)
            .reduce((a, g) => a + (tally[g.id] || 0), 0);

  const markedRows = data.sheets.filter(sh => marks[sh.id]);
  // Смена принимается целиком, плюс то, что склад насчитал сверх MES
  const accepted = data.pending + extra;

  // Печатаем через скрытый iframe: новое окно браузер у склада блокирует,
  // а печать должна начинаться по нажатию, без второго клика.
  const printOne = () => {
    const code = labelCode.trim();
    if (!code) return;
    sendToPrinter(sheetAPI.labelByCodeUrl(code));
    setLabelCode('');
  };

  const printLabels = () => sendToPrinter(sheetAPI.acceptanceLabelsUrl(jobId));

  const sendToPrinter = (url) => {
    const fr = document.createElement('iframe');
    fr.style.display = 'none';
    fr.src = url;
    fr.onload = () => {
      try { fr.contentWindow.focus(); fr.contentWindow.print(); } catch (e) {}
      setTimeout(() => fr.remove(), 60000);
    };
    document.body.appendChild(fr);
  };

  const fixQuantity = async () => {
    const n = parseInt(fixQty, 10);
    if (isNaN(n) || n < 0) { toast.error(t('Количество указано неверно')); return; }
    if (n === data.pending) { setFixOpen(false); return; }
    if (!await confirmToast(
      `Было ${data.pending} шт, стало ${n} шт.\n`
      + 'Расход ДСП и плёнки пересчитается по новому числу.',
      { title: 'Пересчитали стопку', confirmText: 'Исправить', danger: true })) return;
    setFixing(true);
    try {
      await sheetAPI.fixAcceptanceQty(jobId, n, 'пересчитали стопку на складе');
      toast.success(`Выпуск исправлен: ${data.pending} → ${n}`);
      setFixOpen(false);
      // Перечитываем смену: изменились и листы, и сорта, и остаток
      const r = await sheetAPI.acceptanceJob(jobId);
      setData(r.data);
      setMarks({}); setGcnt({}); setExtra(0);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Не удалось исправить');
    } finally { setFixing(false); }
  };

  const submit = async () => {
    if (accepted <= 0) { toast.error(t('Принимать нечего')); return; }

    // Брак материала называем вслух перед подтверждением. Эти цифры теперь
    // списывают плёнку и плиту со склада — ровно ту, которой работала смена.
    // Опечатка в поле уносит остаток молча, и заметят её при инвентаризации,
    // через месяц. Поэтому — вслух, вместе с количеством.
    const matText = filmMaterials
      .map(m => ({ name: m.name, n: Number(mats[m.grade_type_id] ?? m.quantity) || 0 }))
      .filter(x => x.n > 0)
      .map(x => `${x.name}: ${x.n}`)
      .join(', ');

    if (noLabel && nlRest < 0) {
      toast.error(`Принимается ${accepted} листов, а сортов указано ${nlUsed}`);
      return;
    }

    // Сравниваем итоги, а не строки: пресс сорт не называл. Иначе приёмка
    // спрашивала про расхождение каждый раз, когда склад делал свою работу.
    const diff = hintGrade
      ? hintN !== nlTotal
      : Object.keys(opMap).some(g => (opMap[g] || 0) !== (tally[g] || 0));
    // Сорта называем вслух: без бирки лист обезличен, и после приёмки уже не
    // разобрать, какой именно стал вторым. Ошибку ловим здесь или нигде.
    const gText = noLabel
      ? nlGrades.filter(g => nlNum(g.id) > 0)
                .map(g => `${g.name}: ${nlNum(g.id)}`).join(', ')
      : '';
    const q = noLabel
      ? (gText ? `Принять ${accepted} шт? ${gText}, остальное 1-м сортом.`
               : `Принять ${accepted} шт 1-м сортом?`)
      : (diff
        ? `Пресс насчитал не 1-го сорта: ${hintN}, у вас ${nlTotal}. Принять ${accepted} шт по ВАШЕМУ решению?`
        : `Принять ${accepted} шт на склад?`);
    if (!await confirmToast(
      matText ? `${q}\n\nБрак материала — ${matText}. Списать со склада?` : q,
      { confirmText: 'Принять' })) return;
    setSaving(true);
    try {
      // Отправляем только плёнку: ДСП с этого экрана не трогаем, и молча
      // обнулить чужую цифру нельзя.
      const filmOnly = {};
      filmMaterials.forEach(m => {
        filmOnly[m.grade_type_id] = Number(mats[m.grade_type_id] ?? m.quantity) || 0;
      });
      await sheetAPI.accept({
        press_job_id: jobId,
        // Количество шлём только когда насчитали больше MES; иначе — вся смена
        ...(extra > 0 ? { quantity: accepted } : {}),
        materials: filmOnly,
        sheet_grades: noLabel
          ? []
          : markedRows.map(sh => ({ sheet_id: sh.id, grade_type_id: marks[sh.id] })),
        grade_counts: noLabel
          ? nlGrades.reduce((a, g) => {
              if (nlNum(g.id) > 0) a[g.id] = nlNum(g.id);
              return a;
            }, {})
          : {},
        // Причину шлём только там, где она есть. Пустую не выдумываем:
        // «не выбрана» — это ответ, и в разборе он стоит своей строкой.
        grade_reasons: noLabel
          ? nlGrades.reduce((a, g) => {
              if (nlNum(g.id) > 0 && greason[g.id]) a[g.id] = greason[g.id];
              return a;
            }, {})
          : {},
      });
      toast.success(t('✅ Принято на склад'));
      try { sessionStorage.removeItem(draftKey); } catch (e) {}
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка приёмки');
    } finally { setSaving(false); }
  };

  const BOX = { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' };
  const H = { color: 'var(--dim)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 };

  // Закрываем только по крестику. Тёмное поле вокруг окна закрывало его от
  // любого касания — а приёмщик работает стоя, с планшетом в одной руке и
  // сканером в другой, и мимо кнопки попадает постоянно.
  const askClose = async () => {
    const dirty = Object.keys(marks).length > 0
      || Object.keys(gcnt).some(k => nlNum(k) > 0);
    if (dirty && !await confirmToast(
      'Отмеченные сорта не сохранены. Закрыть приёмку?', { confirmText: 'Закрыть' })) return;
    if (!dirty) { try { sessionStorage.removeItem(draftKey); } catch (e) {} }
    onClose();
  };

  return (
    <div style={S.modalBack}>
      <div style={{ ...S.modalBox, maxWidth: 1000 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--text)', fontWeight: 800, fontSize: 17 }}>{data.product_name}</div>
            <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 3 }}>
              {data.press_name} · {data.operator} · {data.texture_name}
              {data.order_number !== '—' && <> · {data.order_number}</>}
            </div>
          </div>
          <SourceTag source={data.source} runs={data.runs} />
          <button onClick={askClose} style={{ background: 'none', border: 'none', color: 'var(--dim)', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        <div className="accept-grid">
          {/* ── Слева: сканирование и сорта ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <div style={BOX}>
              <div style={H}>
                {noLabel ? 'Сорт листа — сколько какого' : 'Сорт листа — сканируйте бирку'}
              </div>

              {/* Бирок в цеху нет — приёмщик пишет числа. Высший сорт не
                  спрашиваем: он и есть остаток, и складывать его в уме
                  приёмщику незачем. Он же сразу и виден внизу. */}
              {noLabel && (
                <div>
                  <div style={{ color: 'var(--muted)', fontSize: 12.5, marginBottom: 10,
                                lineHeight: 1.45 }}>
                    Впишите, сколько листов какого сорта. Остальное примется
                    1-м сортом.
                  </div>
                  {/* Пресс говорит СКОЛЬКО, склад — КАКОГО СОРТА. Поэтому
                      подсказка стоит над всеми полями, а не у одного из
                      них: она относится к любому из сортов. */}
                  {hintN > 0 && (
                    <div style={{
                      background: 'var(--bg)', border: '1px solid var(--border)',
                      borderRadius: 8, padding: '8px 12px', marginBottom: 10,
                      color: 'var(--text)', fontSize: 13, lineHeight: 1.45,
                    }}>
                      Пресс отметил не 1-м сортом: <b>{hintN}</b> — поставьте им сорт
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {nlGrades.map(g => (
                      <React.Fragment key={g.id}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                        }}>
                        <span style={{ flex: 1, color: 'var(--text)', fontSize: 15,
                                       fontWeight: 600 }}>{g.name}</span>
                        {opMap[g.id] > 0 && (
                          <span style={{ color: 'var(--dim)', fontSize: 12,
                                         whiteSpace: 'nowrap' }}>
                            пресс: {opMap[g.id]}
                          </span>
                        )}
                        <input type="text" inputMode="numeric"
                          value={gcnt[g.id] ?? ''}
                          onChange={e => {
                            const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 5);
                            setGcnt(p => ({ ...p, [g.id]: v }));
                          }}
                          onFocus={e => e.target.select()}
                          placeholder="0"
                          style={{ ...S.input, width: 96, textAlign: 'center',
                                   fontSize: 18, fontWeight: 800, padding: '10px 8px' }} />
                        </div>
                        {/* Причина спрашивается только когда сорт поставили.
                            Пузырь — это температура и выдержка, непроклей —
                            давление, царапина — как везли: одно и то же число
                            означает три разных решения. Не выбрал — идём
                            дальше, приёмку это не держит. */}
                        {nlNum(g.id) > 0 && reasons.length > 0 && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap',
                                        margin: '2px 0 10px' }}>
                            {reasons.map(rs => {
                              const on = String(greason[g.id]) === String(rs.id);
                              return (
                                <button key={rs.id} type="button"
                                  onClick={() => setGreason(p => ({
                                    ...p, [g.id]: on ? undefined : rs.id }))}
                                  style={{
                                    background: on ? 'var(--accent)' : 'var(--bg)',
                                    border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
                                    borderRadius: 8, padding: '6px 11px',
                                    color: on ? '#fff' : 'var(--muted)',
                                    fontSize: 12.5, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: 'inherit',
                                  }}>{rs.icon} {rs.name}</button>
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  <div style={{
                    marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  }}>
                    <span style={{ color: 'var(--muted)', fontSize: 14 }}>
                      1-м сортом:
                    </span>
                    <b style={{ fontSize: 20, color: nlRest < 0 ? '#ef4444' : '#22c55e' }}>
                      {nlRest}
                    </b>
                    <span style={{ color: 'var(--dim)', fontSize: 12.5 }}>
                      из {accepted} шт
                    </span>
                  </div>
                  {nlRest < 0 && (
                    <div style={{
                      marginTop: 8, padding: '10px 12px', borderRadius: 9,
                      background: '#ef444414', border: '1px solid #ef444455',
                      color: 'var(--text)', fontSize: 13,
                    }}>
                      ⚠ Сортов указано больше, чем принимается листов
                      ({nlUsed} из {accepted}).
                    </div>
                  )}
                </div>
              )}

              {!noLabel && (
                <input
                  ref={inputRef} value={code}
                  onChange={e => setCode(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); scan(code); } }}
                  placeholder={t('Код листа…')}
                  style={{ ...S.input, fontFamily: 'monospace', fontSize: 15 }}
                />
              )}
              {/* Чужая бирка: не тост на полторы секунды, а плашка, которая
                  висит до следующего скана. В цеху тост уходит незамеченным,
                  а приёмщик уверен, что лист отмечен. */}
              {reject && (
                <div style={{
                  marginTop: 10, padding: '12px 14px', borderRadius: 10,
                  /* Бирка без листа — не ошибка сканера, а объяснение: бумага
                     напечаталась, доски за ней нет. Красным её красить нельзя,
                     иначе приёмщик несёт её начальнику как поломку. */
                  background: SOFT_REJECT.includes(reject.reason) ? '#f59e0b18' : '#ef444418',
                  border: '2px solid ' + (SOFT_REJECT.includes(reject.reason) ? '#f59e0b' : '#ef4444'),
                }}>
                  <div style={{
                    color: SOFT_REJECT.includes(reject.reason) ? '#b45309' : '#ef4444',
                    fontWeight: 800, fontSize: 15,
                  }}>
                    {reject.reason === 'no_sheet'
                      ? '🏷 ' + (reject.label?.state_title || 'Бирка без листа')
                      : reject.reason === 'voided'
                        ? '🏷 Не в счёте смены'
                        : (reject.reason === 'other_job'
                            ? '⛔ Лист из другой смены' : '⛔ Лист не принят')}
                  </div>
                  {reject.code && (
                    <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15, color: 'var(--text)', marginTop: 5 }}>
                      {reject.code}
                    </div>
                  )}
                  <div style={{ color: 'var(--text)', fontSize: 13.5, marginTop: 5, lineHeight: 1.45 }}>
                    {reject.message}
                  </div>
                  {reject.other && (
                    <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>
                      Смена #{reject.other.job_id} · {reject.other.press} · {reject.other.operator}
                      <br />Откройте её приёмку — там этот лист на месте.
                    </div>
                  )}
                  {/* Код выдавали мы — значит известно, чей он и когда вышел
                      из принтера. Приёмщику этого хватает, чтобы отложить
                      бумагу и не идти с ней к начальнику. */}
                  {reject.reason === 'no_sheet' && reject.label && (
                    <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>
                      {reject.label.press_name} · {reject.label.product_name}
                      {reject.label.press_job?.operator
                        && ' · ' + reject.label.press_job.operator}
                      {reject.label.issued_at && (
                        <><br />Бирка выдана {new Date(reject.label.issued_at)
                          .toLocaleString('ru-RU', { day: '2-digit', month: '2-digit',
                            hour: '2-digit', minute: '2-digit' })}</>
                      )}
                    </div>
                  )}
                  <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 6 }}>
                    {reject.reason === 'no_sheet'
                      ? 'Бирка прочиталась, доски за ней нет. Сканируйте следующую.'
                      : 'Ничего не отмечено. Сканируйте следующий лист.'}
                  </div>
                </div>
              )}
              {found && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ color: 'var(--text)', fontSize: 14, marginBottom: 8 }}>
                    <b style={{ fontFamily: 'monospace' }}>{found.code}</b>
                    {' · сейчас: '}<b>{found.grade_name}</b>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))', gap: 8 }}>
                    {grades.map(g => (
                      <button key={g.id} onClick={() => assign(g.id)} className="touch-btn" style={{
                        background: 'var(--card)', border: '2px solid var(--border)',
                        borderRadius: 10, color: 'var(--text)', padding: '12px 6px',
                        fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                      }}>{g.name}</button>
                    ))}
                  </div>
                </div>
              )}
              {markedRows.length > 0 && (
                <div style={{ marginTop: 12, maxHeight: 150, overflowY: 'auto' }}>
                  {markedRows.map(sh => (
                    <div key={sh.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '5px 0', borderTop: '1px solid var(--border)', fontSize: 13,
                    }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{sh.code}</span>
                      <span style={{ color: 'var(--dim)' }}>{sh.grade_name} →</span>
                      <b style={{ color: 'var(--accent)', flex: 1 }}>{gradeName(marks[sh.id])}</b>
                      {/* Ошибся кнопкой — снимается тут же, до подтверждения */}
                      <button onClick={() => unmark(sh.id)} style={{
                        background: 'none', border: 'none', color: '#ef4444',
                        cursor: 'pointer', fontSize: 15,
                      }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Пресс против склада: видно, где разошлись */}
            <div style={BOX}>
              <div style={H}>Сорта: пресс сказал / склад принял</div>
              {/* Пресс называет одно число — сколько листов не высшие. Сорт
                  им ставит склад, и сходиться должны итоги, а не строки:
                  «Не высший: 2» против «1-й сорт: 2» — это согласие, а не
                  расхождение. Раньше они стояли двумя строками и обе
                  горели жёлтым. */}
              {hintN > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8,
                  paddingBottom: 8, borderBottom: '1px solid var(--border)', fontSize: 13,
                }}>
                  <span style={{ color: 'var(--muted)', flex: 1 }}>Не 1-го сорта, всего</span>
                  <span style={{ color: 'var(--dim)' }}>{hintN}</span>
                  <span style={{ color: 'var(--border)' }}>→</span>
                  <b style={{
                    color: hintN === nlTotal ? 'var(--text)' : '#f59e0b',
                    minWidth: 26, textAlign: 'right',
                  }}>{nlTotal}</b>
                </div>
              )}
              {grades.map(g => {
                if (g.is_press_hint) return null;
                const op = opMap[g.id] || 0;
                const wh = tally[g.id] || 0;
                if (!op && !wh) return null;
                return (
                  <div key={g.id} style={{
                    display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 5, fontSize: 13,
                  }}>
                    <span style={{ color: 'var(--muted)', flex: 1 }}>{g.name}</span>
                    <span style={{ color: 'var(--dim)' }}>{op}</span>
                    <span style={{ color: 'var(--border)' }}>→</span>
                    <b style={{ color: op === wh ? 'var(--text)' : '#f59e0b', minWidth: 26, textAlign: 'right' }}>{wh}</b>
                  </div>
                );
              })}
              <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 6, lineHeight: 1.4 }}>
                Отметки пресса — подсказка. Итоговый сорт решает склад.
              </div>
            </div>
          </div>

          {/* ── Справа: брак материала, количество, кнопки ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            {/* Только плёнка. ДСП отсюда убрали: смену сдают по нескольку раз
                за день, а битые плиты складывают в один угол и считают
                вечером, когда угол разберут. Делить эту кучу на смены по
                памяти кладовщику не под силу — для ДСП свой экран, по дню. */}
            {filmMaterials.length > 0 && (
              <div style={BOX}>
                {/* У пресса этих кнопок больше нет: оператор в рукавицах
                    отвечает на один вопрос — годный лист или брак. Плёнку и
                    плиту, испорченные ДО пресса, считает приёмщик, пересчитывая
                    пачку руками. Его цифра и списывает сырьё со склада. */}
                <div style={H}>Брак материала — сколько испорчено</div>
                {filmMaterials.map(m => (
                  <div key={m.grade_type_id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
                  }}>
                    <span style={{ color: 'var(--muted)', fontSize: 13, flex: 1 }}>{m.name}</span>
                    {m.quantity > 0 && (
                      <span style={{ color: 'var(--dim)', fontSize: 12 }}>пресс: {m.quantity}</span>
                    )}
                    {/* Пустое поле вместо нуля. С нулём каждая цифра
                        приписывалась к нему справа — «0» плюс «3» давало «03»,
                        и ноль приходилось стирать отдельным движением. */}
                    <input
                      type="text" inputMode="numeric"
                      value={(() => { const v = mats[m.grade_type_id] ?? m.quantity;
                                      return v ? String(v) : ''; })()}
                      onChange={e => setMats(p => ({
                        ...p,
                        [m.grade_type_id]: Number(e.target.value.replace(/[^0-9]/g, '').slice(0, 4)) || 0,
                      }))}
                      onFocus={e => e.target.select()}
                      placeholder="0"
                      style={{ ...S.input, width: 84, textAlign: 'center', fontWeight: 700 }}
                    />
                  </div>
                ))}
                <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 4,
                              lineHeight: 1.45 }}>
                  Здесь цифра пресса — пересчитайте пачку и поправьте. Спишется
                  та плёнка, которой работала эта смена. Ничего не испорчено —
                  оставьте пусто.
                </div>
                {(data.material_history || []).length > 0 && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                    <div style={{ color: 'var(--dim)', fontSize: 11, marginBottom: 4 }}>Правили раньше:</div>
                    {data.material_history.slice(0, 5).map(h => (
                      <div key={h.id} style={{ color: 'var(--dim)', fontSize: 11, marginBottom: 2 }}>
                        {h.name}: {h.from} → <b style={{ color: 'var(--muted)' }}>{h.to}</b> · {h.by}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Смена принимается целиком. Раньше тут стояло поле «сколько»,
                но оно резало листы по счётчику: система откладывала первые N
                по времени, а какие именно четыре листа не доехали — никто не
                выбирал. Их бирки навсегда оставались в «ждёт приёмки», хотя
                физически на складе лежали совсем другие.

                Оператор действительно может пережать +1 — тогда лишний ЛДСП
                списывают со склада отдельно. Списание видно как списание,
                а не как смена, которая так и не закрылась. */}
            <div style={BOX}>
              <div style={H}>Сколько принимаем</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ color: 'var(--text)', fontSize: 30, fontWeight: 800 }}>{data.pending}</span>
                <span style={{ color: 'var(--muted)', fontSize: 14 }}>шт — вся смена</span>
                {extra > 0 && (
                  <span style={{ color: '#b45309', fontSize: 16, fontWeight: 800 }}>
                    +{extra} сверх MES
                  </span>
                )}
              </div>
              {/* В стопке больше, чем насчитал MES (листы из «Разбора» сами не
                  создаются — количество решает склад). Недостающие заведутся
                  при приёмке и войдут в приход как обычные. */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                            flexWrap: 'wrap', marginTop: 10 }}>
                <span style={{ color: 'var(--dim)', fontSize: 12.5 }}>Сверх MES:</span>
                <button onClick={() => setExtra(x => Math.max(0, x - 1))}
                  disabled={extra <= 0} style={{
                    ...S.input, width: 44, padding: '8px 0', cursor: 'pointer',
                    fontWeight: 800, color: '#f59e0b', borderColor: '#f59e0b55',
                    opacity: extra <= 0 ? 0.5 : 1 }}>−</button>
                <input type="text" inputMode="numeric" value={extra}
                  onChange={e => setExtra(parseInt(e.target.value.replace(/[^0-9]/g, '').slice(0, 4), 10) || 0)}
                  onFocus={e => e.target.select()}
                  style={{ ...S.input, width: 72, textAlign: 'center',
                           fontSize: 18, fontWeight: 800, padding: '6px 4px' }} />
                {[1, 5].map(d => (
                  <button key={d} onClick={() => setExtra(x => Math.min(1000, x + d))}
                    style={{
                      ...S.input, width: 44, padding: '8px 0', cursor: 'pointer',
                      fontWeight: 800, color: '#22c55e', borderColor: '#22c55e55' }}>+{d}</button>
                ))}
              </div>
              {/* Печать бирок — только тем листам, которым их не печатали у
                  пресса. На остальных бирки наклеены ещё в цеху, и печатать
                  всю стопку незачем. Кнопки нет, пока печать не включили в
                  настройках: принтера может не быть вовсе. Приёмка от неё не
                  зависит — стопку принимают по числу. */}
              {data.label_print && data.labels_needed > 0 && (
                <button onClick={printLabels} className="touch-btn" style={{
                  marginTop: 10, width: '100%', background: 'var(--card)',
                  border: '1px dashed var(--border)', borderRadius: 10,
                  color: 'var(--muted)', padding: '10px 0', fontSize: 13.5,
                  fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                }}>🏷 Бирки на добавленные листы · {data.labels_needed} шт</button>
              )}

              {/* Бирка живёт на доске месяцами: её трут, мочат, задевают краем
                  соседнего листа. QR перестаёт читаться первым — он мелкий, —
                  а цифры под ним ещё видны. Тогда код набирают руками. */}
              {data.label_print && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input value={labelCode}
                    onChange={e => setLabelCode(e.target.value.toUpperCase().slice(0, 40))}
                    onKeyDown={e => { if (e.key === 'Enter') printOne(); }}
                    placeholder={t('код листа — если бирка испортилась')}
                    style={{ ...S.input, flex: 1, fontFamily: 'monospace',
                             letterSpacing: 1 }} />
                  <button onClick={printOne} disabled={!labelCode.trim()}
                    className="touch-btn" style={{
                      background: 'var(--card)', border: '1px dashed var(--border)',
                      borderRadius: 10, color: 'var(--muted)', padding: '0 16px',
                      fontSize: 15, fontFamily: 'inherit',
                      cursor: labelCode.trim() ? 'pointer' : 'not-allowed',
                      opacity: labelCode.trim() ? 1 : 0.5,
                    }}>🏷</button>
                </div>
              )}

              {/* Пресс жмут и вхолостую — плиту чистят, режим проверяют.
                  Раньше это выяснялось на приёмке как расхождение и разбиралось
                  по памяти. Теперь станок сказал сам, и спорить не о чем. */}
              {data.strokes && data.strokes.cycles != null
                && data.strokes.cycles !== data.pending && (
                <div style={{
                  marginTop: 8, background: 'var(--bg)',
                  border: '1px solid var(--border)', borderRadius: 8,
                  padding: '7px 10px', fontSize: 12.5, color: 'var(--muted)',
                  lineHeight: 1.5,
                }}>
                  Пресс сомкнулся <b style={{ color: 'var(--text)' }}>{data.strokes.cycles}</b> раз
                  {data.strokes.idle > 0 && (
                    <>, из них <b style={{ color: '#b45309' }}>{data.strokes.idle}</b> вхолостую</>
                  )}
                  {' '}· сдано <b style={{ color: 'var(--text)' }}>{data.quantity_done}</b>
                </div>
              )}

              {/* Ночная смена уходит домой, а утром приёмщик пересчитывает
                  стопку и находит расхождение. Спросить уже не у кого: пресс
                  правит свою цифру только пока смену не забрали, а до утра
                  оператора нет. Приёмщик оставался со стопкой, в которой
                  листов не столько, и без единой кнопки. */}
              {/* Смену уже правили — показываем это до приёмки, а не после:
                  человек должен видеть, что число уже двигали, прежде чем
                  двигать его снова. */}
              {(data.quantity_history || []).map((c, i) => (
                <div key={i} style={{
                  marginTop: 8, background: '#f59e0b14',
                  border: '1px solid #f59e0b44', borderRadius: 8,
                  padding: '6px 10px', fontSize: 12.5, color: 'var(--text)',
                }}>
                  <b style={{ color: '#b45309' }}>{c.from} → {c.to}</b>
                  <span style={{ color: 'var(--dim)' }}>
                    {' · '}{c.by}{c.reason ? ' · ' + c.reason : ''}
                  </span>
                </div>
              ))}

              {!fixOpen ? (
                <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 8, lineHeight: 1.45 }}>
                  Число не сходится?{' '}
                  <button onClick={() => { setFixOpen(true); setFixQty(String(data.pending)); }}
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      color: 'var(--accent)', fontWeight: 700, fontSize: 12,
                      cursor: 'pointer', fontFamily: 'inherit',
                      textDecoration: 'underline',
                    }}>Пересчитать стопку</button>
                  {' '}— расход ДСП и плёнки пересчитается сам.
                </div>
              ) : (
                <div style={{ marginTop: 10, paddingTop: 10,
                              borderTop: '1px solid var(--border)' }}>
                  <div style={{ color: 'var(--muted)', fontSize: 12.5, marginBottom: 8 }}>
                    Сколько листов в стопке на самом деле?
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                                flexWrap: 'wrap' }}>
                    {[-5, -1].map(d => (
                      <button key={d} onClick={() => setFixQty(String(Math.max(0,
                        (parseInt(fixQty, 10) || 0) + d)))} style={{
                        ...S.input, width: 56, padding: '10px 0', cursor: 'pointer',
                        fontWeight: 800, color: '#f59e0b', borderColor: '#f59e0b55',
                      }}>{d}</button>
                    ))}
                    <input type="text" inputMode="numeric" value={fixQty}
                      onChange={e => setFixQty(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
                      onFocus={e => e.target.select()}
                      style={{ ...S.input, width: 96, textAlign: 'center',
                               fontSize: 22, fontWeight: 800, padding: '8px 6px' }} />
                    {[1, 5].map(d => (
                      <button key={d} onClick={() => setFixQty(String(
                        (parseInt(fixQty, 10) || 0) + d))} style={{
                        ...S.input, width: 56, padding: '10px 0', cursor: 'pointer',
                        fontWeight: 800, color: '#22c55e', borderColor: '#22c55e55',
                      }}>+{d}</button>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button onClick={() => setFixOpen(false)} style={{
                      ...S.input, width: 'auto', padding: '10px 16px',
                      cursor: 'pointer', color: 'var(--muted)',
                    }}>Отмена</button>
                    <button onClick={fixQuantity} disabled={fixing} className="touch-btn"
                      style={{
                        flex: 1, background: fixing ? 'var(--bg)' : '#f59e0b',
                        border: 'none', borderRadius: 10,
                        color: fixing ? 'var(--dim)' : '#fff', padding: '11px 0',
                        fontSize: 14.5, fontWeight: 800, fontFamily: 'inherit',
                        cursor: fixing ? 'not-allowed' : 'pointer',
                      }}>{fixing ? '…' : 'Исправить выпуск'}</button>
                  </div>
                  <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
                    Правка записывается на вас и остаётся в истории смены.
                    Сырьё спишется по новому числу.
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 'auto' }}>
              <button onClick={onClose} style={{
                flex: 1, background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 10, color: 'var(--muted)', padding: '13px 0',
                fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
              }}>Отмена</button>
              <button onClick={submit} disabled={saving} className="touch-btn" style={{
                flex: 2, background: saving ? 'var(--bg)' : '#22c55e', border: 'none',
                borderRadius: 10, color: saving ? 'var(--dim)' : '#fff', padding: '13px 0',
                fontSize: 15, fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}>{saving ? 'Приём…' : `✅ Принять на склад (${accepted})`}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShopAcceptanceTab({ onAccepted, onGo }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  // Восемнадцать смен ждут приёмки, и кладовщик ищет в них конкретную —
  // ту, чью стопку он сейчас держит в руках
  const [q, setQ]             = useState('');
  // Приёмка идёт в отдельном окне: решений внутри несколько и путать их нельзя
  const [openJob, setOpenJob] = useState(null);
  // Уже принятое. Очередь показывает только то, что ЕЩЁ не приняли: смену
  // забрали — и она исчезла с экрана. А спрашивают ровно про принятое:
  // «эту партию вчера завели?», «сколько за неделю», «кто принимал».
  const [done, setDone]   = useState([]);
  const [from, setFrom]   = useState('');
  const [to, setTo]       = useState('');
  const [openDone, setOpenDone] = useState(null);
  // Две разные работы на одном экране мешали друг другу: очередь — это то,
  // что надо сделать сейчас, а принятое — справка «мы это завели?». Одно
  // листают до конца, во второе заглядывают. Разводим по вкладкам.
  const [tab, setTab] = useState('wait');
  // Прессов в цехе несколько, и смотрят обычно по одному: «что там у второго».
  // Фильтр один на обе вкладки — переключаясь между ними, кладовщик держит в
  // голове тот же пресс, и сбрасывать выбор под ним нельзя.
  const [presses, setPresses] = useState([]);
  const [pressId, setPressId] = useState('');

  // Спиннер — только на ПЕРВОЙ загрузке. Раньше фоновое обновление раз в
  // полминуты ставило loading=true, и весь раздел подменялся словом
  // «Загрузка…». Вместе с разделом размонтировалось и окно приёмки: приёмщик
  // бил сканером по стопке, а на очередной секунде окно открывалось заново —
  // пустое. Отсканированные листы и пересчитанный брак пропадали молча.
  const load = useCallback((first = false) => {
    if (first) setLoading(true);
    sheetAPI.acceptance()
      .then(r => setRows(r.data.results || []))
      .catch(() => toast.error(t('Приёмка не загрузилась')))
      .finally(() => { if (first) setLoading(false); });
  }, []);

  useEffect(() => { load(true); }, [load]);

  useEffect(() => {
    productionAPI.getPresses()
      .then(r => setPresses(r.data.results || r.data || []))
      .catch(() => {});
  }, []);

  const loadDone = useCallback(() => {
    const p = { limit: 200 };
    if (from) p.from = from;
    if (to) p.to = to;
    // Пресс отдаём на сервер, а не режем на клиенте: список принятого
    // ограничен последними двумя сотнями, и по одному прессу за месяц в них
    // попадёт не всё.
    if (pressId) p.press = pressId;
    sheetAPI.acceptanceDone(p)
      .then(r => setDone(r.data.results || []))
      .catch(() => {});
  }, [from, to, pressId]);
  useEffect(() => { loadDone(); }, [loadDone]);

  // Пресс допрессовывает позицию не по расписанию — список подтягиваем сами.
  // Но пока открыто окно приёмки, не трогаем ничего: список за окном всё равно
  // не виден, а любая перерисовка стоит приёмщику набранной смены.
  useEffect(() => {
    if (openJob) return undefined;
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load, openJob]);

  // Ссылки должны быть постоянными: окно приёмки перечитывает смену, когда
  // меняется onClose, а новая функция на каждый рендер означала перечитывание
  // по любому поводу — с затиранием уже введённых цифр брака.
  const closeJob = useCallback(() => setOpenJob(null), []);
  const doneJob = useCallback(() => {
    setOpenJob(null);
    load();
    loadDone();
    if (onAccepted) onAccepted();
  }, [load, loadDone, onAccepted]);

  if (loading) return <p style={{ color: 'var(--dim)', padding: 24 }}>{t('Загрузка…')}</p>;

  // Пустая очередь раньше подменяла собой весь раздел, вместе со списком
  // принятого. А смотрят его как раз тогда, когда принимать уже нечего:
  // «что мы сегодня завели».
  const emptyQueue = !rows.length;

  // Фильтры: смена может быть завершена, а может быть просто отложена —
  // пресс перешёл на другую позицию. Это разные случаи, и разбирают их
  // по-разному.
  // Ищем по любому куску, в любом порядке: «дуб 012» должно находить
  // «Дуб Вотан … #LT-20260819-012». Держать в голове точное начало строки
  // приёмщик не обязан.
  const hit = makeMatcher(q);
  const shown = rows.filter(r => {
    if (pressId && String(r.press_id) !== String(pressId)) return false;
    return hit(r.press_name, r.product_name, r.texture_name,
               r.order_number, r.operator);
  });

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {openJob && (
        <AcceptanceModal jobId={openJob} onClose={closeJob} onDone={doneJob} />
      )}

      {/* Вкладки стоят в верхней строке — отдельной полосой они съедали ещё
          одну, а на телефоне работа начинается ниже. */}
      <BackHome onGo={onGo} title={t('Приёмка из цеха')} tab={tab} onTab={setTab}
        tabs={[
          { key: 'wait', label: t('Ждут приёмки'), count: rows.length },
          { key: 'done', label: t('Принято'), count: done.length },
        ]} />

      {tab === 'wait' && emptyQueue && !pressId && (
        <div style={{ ...S.card, textAlign: 'center', padding: '32px 20px' }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>✅</div>
          <div style={{ color: 'var(--text)', fontWeight: 700, marginBottom: 6 }}>
            {t('Всё принято')}
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 13 }}>
            {t('Когда пресс допрессует позицию и перейдёт на другую — она появится здесь.')}
          </div>
        </div>
      )}

      {/* Пресс — один на обе вкладки: переключаясь, кладовщик держит в голове
          тот же станок. Стоит выше вкладочного содержимого. */}
      {presses.length > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                      flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--dim)', fontSize: 12.5 }}>{t('Пресс')}</span>
          <select value={pressId} onChange={e => setPressId(e.target.value)}
            style={{ ...S.input, width: 'auto', minWidth: 170, padding: '7px 10px',
                     fontSize: 13.5 }}>
            <option value="">{t('все прессы')}</option>
            {presses.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {pressId && (
            <button onClick={() => setPressId('')} style={{
              background: 'none', border: 'none', color: 'var(--dim)',
              cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' }}>
              {t('сбросить')}
            </button>
          )}
        </div>
      )}

      <div style={{ display: (tab === 'wait' && emptyQueue) ? 'none' : 'flex',
                    gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder={t('Пресс, продукт, заказ, оператор…')}
          style={{
            flex: '1 1 240px', minWidth: 200, padding: '9px 13px',
            background: 'var(--input)', border: '1px solid var(--border)',
            borderRadius: 9, color: 'var(--text)', fontSize: 13.5,
            outline: 'none', fontFamily: 'inherit',
          }} />
        {/* Счётчик убран: он теперь стоит на самой вкладке, а второй раз
            то же число только занимает строку. */}
      </div>

      {tab === 'wait' && shown.length === 0 && (!emptyQueue || pressId) && (
        <div style={{ ...S.card, color: 'var(--dim)', textAlign: 'center', padding: 24 }}>
          {t('По этому условию ничего нет')}
        </div>
      )}

      {/* Строка вместо карточки. Карточка занимала полтораста точек: пресс,
          продукт, оператор, сорта, материалы и кнопка — всё столбиком. При
          восемнадцати ждущих сменах это три экрана прокрутки, а решение по
          каждой принимают внутри её окна, не в списке. */}
      {tab === 'wait' && shown.map(r => (
        <div key={r.job_id} style={{
          ...S.card, borderLeft: '3px solid #22c55e',
          padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ flex: '1 1 260px', minWidth: 0, fontSize: 13.5 }}>
            <b style={{ color: 'var(--text)' }}>{r.press_name}</b>
            <span style={{ color: 'var(--text)' }}> · {r.product_name}</span>
            <span style={{ color: 'var(--dim)' }}> · {r.texture_name}</span>
            {r.order_number && r.order_number !== '—' && (
              <span style={{ color: '#f59e0b' }}> · #{r.order_number}</span>
            )}
            <span style={{ display: 'block', color: 'var(--dim)', fontSize: 11.5, marginTop: 2 }}>
              {r.operator}
              {r.completed_at ? ' · ' + new Date(r.completed_at).toLocaleString('ru-RU') : ''}
            </span>
          </span>

          <SourceTag source={r.source} runs={r.runs} />

          {r.late_sheets > 0 && r.late_date && (
            <span title={t('Часть листов добавлена после закрытия смены — из «Разбора с прессов»')}
              style={{ background: '#f59e0b18', color: '#f59e0b', borderRadius: 6,
                       padding: '2px 8px', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
              дослано из разбора {r.late_date}
            </span>
          )}

          {/* Что отметил пресс — цифры мелкие, решение принимают в окне */}
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flexShrink: 0 }}>
            {r.grades.map(g => (
              <span key={g.grade_type_id || 0} style={{
                background: g.is_default ? '#22c55e18' : '#f59e0b18',
                color: g.is_default ? '#22c55e' : '#f59e0b',
                borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700,
              }}>{g.name}: {g.count}</span>
            ))}
            {r.materials.map(m => (
              <span key={'m' + m.grade_type_id} style={{
                background: 'var(--bg)', color: 'var(--muted)',
                borderRadius: 6, padding: '2px 8px', fontSize: 12,
              }}>{m.name}: {m.quantity}</span>
            ))}
          </span>

          <span style={{ color: 'var(--dim)', fontSize: 12.5, flexShrink: 0 }}>
            выпущено <b style={{ color: 'var(--text)', fontSize: 14 }}>{r.pending}</b>
          </span>

          <button onClick={() => setOpenJob(r.job_id)} className="touch-btn" style={{
            background: '#22c55e', border: 'none', borderRadius: 9, color: '#fff',
            padding: '9px 18px', fontSize: 13.5, fontWeight: 800,
            cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
          }}>{t('Принять')} →</button>
        </div>
      ))}

      {/* Уже принято. Спрашивают именно про это: «эту партию вчера завели?»,
          «сколько за неделю», «кто принимал». Раньше ответа не было нигде:
          принятая смена просто исчезала с экрана. Период не требуем — обычно
          смотрят последнее, и тогда список идёт от свежего к старому. */}
      <div style={{ display: tab === 'done' ? 'block' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                      marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--dim)', fontSize: 12.5 }}>{t('с')}</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ ...S.input, width: 150, padding: '6px 9px', fontSize: 13 }} />
          <span style={{ color: 'var(--dim)', fontSize: 12.5 }}>{t('по')}</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ ...S.input, width: 150, padding: '6px 9px', fontSize: 13 }} />
          {(from || to) && (
            <button onClick={() => { setFrom(''); setTo(''); }} style={{
              background: 'none', border: 'none', color: 'var(--dim)',
              cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' }}>
              {t('за всё время')}
            </button>
          )}
          <span style={{ marginLeft: 'auto', color: 'var(--dim)', fontSize: 12.5 }}>
            {done.length} {t('приёмок')} · {done.reduce((a, r) => a + (r.quantity || 0), 0)} {t('листов')}
          </span>
        </div>

        {done.length === 0 ? (
          <div style={{ color: 'var(--dim)', fontSize: 13, padding: '10px 2px' }}>
            {(from || to) ? t('За этот период ничего не принимали')
                          : t('Приёмок пока не было')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {done.filter(r => makeMatcher(q)(
                r.product, r.texture, r.order_number, r.press, r.operator, r.who))
              .map(r => {
              const open = openDone === r.id;
              return (
                <div key={r.id} style={{
                  ...S.card, borderLeft: '3px solid #22c55e',
                  padding: '8px 13px', fontSize: 13,
                  ...openCard(open, '#22c55e'),
                }}>
                  <button onClick={() => setOpenDone(open ? null : r.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    width: '100%', background: 'none', border: 'none', padding: 0,
                    cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                    textAlign: 'left', color: 'var(--text)',
                  }}>
                    <span style={{ color: 'var(--dim)', width: 12, flexShrink: 0 }}>
                      {open ? '▾' : '▸'}
                    </span>
                    <span style={{ flex: '1 1 240px', minWidth: 0 }}>
                      <b style={{ color: 'var(--text)' }}>{r.product}</b>
                      {r.texture && <span style={{ color: 'var(--dim)' }}> · {r.texture}</span>}
                      {r.order_number && (
                        <span style={{ color: '#f59e0b' }}> · #{r.order_number}</span>
                      )}
                    </span>
                    <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      <b style={{ color: 'var(--text)', fontSize: 15 }}>{r.quantity}</b> {t('шт')}
                    </span>
                    <span style={{ color: 'var(--dim)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {r.at ? new Date(r.at).toLocaleString('ru-RU',
                        { day: '2-digit', month: '2-digit', year: '2-digit',
                          hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </button>

                  {open && (
                    <div style={{ ...openDivider('#22c55e'),
                                  fontSize: 12.5, color: 'var(--muted)' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap',
                                    marginBottom: 6 }}>
                        {(r.grades || []).map(g => (
                          <span key={g.name} style={{
                            background: g.is_default ? '#22c55e18' : '#f59e0b18',
                            color: g.is_default ? '#22c55e' : '#f59e0b',
                            borderRadius: 6, padding: '2px 8px', fontWeight: 700,
                          }}>{g.name}: {g.count}</span>
                        ))}
                        {(r.materials || []).map(m => (
                          <span key={'m' + m.name} style={{
                            background: 'var(--bg)', color: 'var(--muted)',
                            borderRadius: 6, padding: '2px 8px',
                          }}>{m.name}: {m.quantity}</span>
                        ))}
                      </div>
                      {/* Правка выпуска — первое, о чём спросят через неделю:
                          «почему приняли 72, а пресс сдавал 75». */}
                      {(r.qty_changes || []).map((c, i) => (
                        <div key={i} style={{
                          display: 'flex', gap: 8, alignItems: 'center',
                          flexWrap: 'wrap', marginBottom: 6,
                          background: '#f59e0b14', border: '1px solid #f59e0b44',
                          borderRadius: 8, padding: '5px 10px', fontSize: 12.5,
                        }}>
                          <b style={{ color: '#b45309' }}>
                            {t('выпуск исправлен')}: {c.from} → {c.to}
                          </b>
                          <span style={{ color: 'var(--dim)' }}>
                            {c.by}
                            {c.reason ? ' · ' + c.reason : ''}
                            {c.at ? ' · ' + new Date(c.at).toLocaleString('ru-RU',
                              { day: '2-digit', month: '2-digit',
                                hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      ))}

                      <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                                    flexWrap: 'wrap' }}>
                        <span>{r.press} · {t('прессовал')}: {r.operator} · {t('принял')}: {r.who}</span>
                        <SourceTag source={r.source} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ГЛАВНАЯ СТРАНИЦА
// ═══════════════════════════════════════════════════════════════════
export default function WarehousePage() {
  const [view, setView]     = useState('home');
  // Сколько работы ждёт — считаем на самой странице: число нужно и главному
  // экрану, и пунктам меню, а запрашивать его дважды незачем.
  const [counts, setCounts] = useState({ acceptance: 0, ship: 0 });
  const [refreshGP, setRefreshGP] = useState(0);

  // Разделы рисуются в общем боковом меню (Layout), под пунктом «Склад ГП»:
  // второе меню внутри страницы занимало место и спорило с первым за внимание.
  // Девять кнопок в ряд не помещались и переносились на вторую строку, съедая
  // высоту у самого содержимого. Разделы сгруппированы по ходу товара:
  // сырьё пришло -> цех сделал -> готовое ушло. Меню сбоку, а не сверху:
  // вертикального места на экране всегда меньше, чем горизонтального.
  // Пресс сдаёт смену не по расписанию, менеджер разрешает отгрузку тоже —
  // числа должны подтягиваться сами, иначе кладовщик узнаёт о работе, только
  // когда откроет раздел.
  useEffect(() => {
    const load = () => {
      sheetAPI.acceptance()
        .then(r => setCounts(c => ({ ...c, acceptance: (r.data.results || []).length })))
        .catch(() => {});
      // Считаем РАСПОРЯЖЕНИЯ, а не готовые заказы. Заказ со статусом «готов»
      // складу больше ничего не говорит: грузят по документу офиса, и пока
      // его нет, работы нет. Плитка показывала 203, а внутри было пусто —
      // кладовщик открывал раздел зря.
      shipNoteAPI.list({ status: 'issued', limit: 200 })
        .then(r => setCounts(c => ({ ...c, ship: (r.data.results || []).length })))
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [refreshGP]);

  const GROUPS = [
    { title: '', items: [
      { key: 'home',      label: '🏠 Дела склада' },
    ]},
    // Приёмка, сорта и отгрузка стоят на главном экране крупными плитками с
    // числом ждущего. В меню они висели вторым списком — те же три дела,
    // мелким шрифтом и без чисел. Здесь остаются остатки: их смотрят, а не
    // делают, и на плитку они не просятся.
    //
    // Трёх пунктов больше нет. «📦 Сырьё», «🏬 По складам» и «📦 Готовая
    // продукция» отвечали на ОДИН вопрос — «сколько чего лежит», — и два из
    // трёх шли с одинаковым значком. Кладовщик выбирал наугад, попадал не в
    // тот, возвращался и пробовал следующий. Теперь одна дверь, а взгляд
    // переключается внутри, где сразу видно все три и не надо угадывать.
    { title: 'Посмотреть', items: [
      { key: 'stock',     label: '📦 Остатки' },
    ]},
  ];

  const go = (key) => setView(key);

  return (
    <Layout title={t("Склад — Управление")} sections={GROUPS} section={view} onSection={go}
            alsoSections={['acceptance', 'regrade', 'ship', 'dspdefect']}>

      {view === 'home' && <HomeScreen counts={counts} onGo={go} />}

      {/* Пересортировка */}
      {view === 'acceptance' && (
        <>
          <ShopAcceptanceTab onGo={go}
            onAccepted={() => setRefreshGP(n => n + 1)} />
        </>
      )}
      {view === 'regrade' && (
        <>
          <BackHome onGo={go} title={t('Сорта')} />
          <RegradeTab />
        </>
      )}
      {view === 'dspdefect' && (
        <>
          <BackHome onGo={go} title={t('Брак ДСП')} />
          <DspDefectTab />
        </>
      )}

      {/* Остатки — одна дверь, три взгляда на один и тот же склад:
          сырьё (от товара), по складам (от места) и готовая продукция.
          Раньше это были три пункта меню, два из них под одним значком. */}
      {view === 'stock' && (
        <>
          <BackHome onGo={go} title={t('Остатки')} />
          <StockLook refresh={refreshGP} onRefresh={() => setRefreshGP(n => n + 1)} />
        </>
      )}

      {/* Отгрузка готовых заказов */}
      {view === 'ship' && (
        <>
          <ShipOrdersTab onGo={go}
            onShipped={() => setRefreshGP(n => n + 1)} />
        </>
      )}

    </Layout>
  );
}
